import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgxExtendedPdfViewerModule, pdfDefaultOptions } from 'ngx-extended-pdf-viewer';
import { finalize } from 'rxjs';

import { ReadOnlyPdfReviewComponent } from '../../components/read-only-pdf-review/read-only-pdf-review.component';
import {
  DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS,
  type BrandSignatureFrameSettings,
} from '../../models/brand.models';
import type { DocumentFieldDraft, SignSessionResponse } from '../../models/document.models';
import type { TemplateTextboxResizeType, TemplateTextboxValidationType } from '../../models/template.models';
import { DocumentApiService } from '../../services/document-api.service';
import { GlobalFeedbackService } from '../../services/global-feedback.service';
import { type UserDateTimePreferences, UserDateTimeFormatService } from '../../services/user-date-time-format.service';
import { normalizeApiError } from '../../utils/error-normalizer';

type SignFieldType = DocumentFieldDraft['type'];
type MarkKind = 'signature' | 'initials';
type MarkTab = 'type' | 'draw' | 'upload';
type ImagePointerState = { pointerId: number; startX: number; startY: number; baseX: number; baseY: number };
type ChoiceFieldType = 'checkbox' | 'radio';
type ChoiceGroupSnapshot = { groupKey: string; fields: DocumentFieldDraft[]; values: string[] };
type FieldNodeKind = 'deferred' | 'mark' | 'image' | 'editable_date' | 'choice' | 'dropdown' | 'text';
type FieldNodeCacheEntry = { page: number; element: HTMLElement; kind: FieldNodeKind };
type TypedMarkStyleOption = {
  id: string;
  fontFamily: string;
  fontWeight: number;
};

interface ResolvedFieldFontSettings {
  family: string;
  size: number;
  lineHeight: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  color: string;
  align: 'left' | 'center' | 'right';
  characterSpacing: number;
  rtl: boolean;
}

interface ResolvedTextboxSettings {
  resizeType: TemplateTextboxResizeType;
  fixedWidth: boolean;
  characterLimit?: number;
  validationType: TemplateTextboxValidationType;
  validationValue?: string;
}

const SUPPORTED_FIELD_TYPES: SignFieldType[] = [
  'signature',
  'initials',
  'textbox',
  'date_signed',
  'checkbox',
  'radio',
  'editable_date',
  'dropdown',
  'image',
  'name',
  'email',
  'title',
  'company',
];

const MARK_TABS: MarkTab[] = ['type', 'draw', 'upload'];
const MAX_MARK_UPLOAD_BYTES = 2 * 1024 * 1024;
const MARK_UPLOAD_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/bmp']);
const TYPE_STYLE_OPTIONS: TypedMarkStyleOption[] = [
  { id: 'dancing-script', fontFamily: '"Dancing Script", cursive', fontWeight: 700 },
  { id: 'great-vibes', fontFamily: '"Great Vibes", cursive', fontWeight: 400 },
  { id: 'pacifico', fontFamily: '"Pacifico", cursive', fontWeight: 400 },
  { id: 'satisfy', fontFamily: '"Satisfy", cursive', fontWeight: 400 },
];
const MARK_TRIM_ALPHA_THRESHOLD = 18;
const MARK_TRIM_LUMA_THRESHOLD = 245;
const MARK_TRIM_PADDING_PX = 6;
const MAX_IMAGE_UPLOAD_BYTES = 25 * 1024 * 1024;
const IMAGE_UPLOAD_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/bmp', 'image/svg+xml']);
const LEGACY_AUTO_GROUP_KEY_PATTERN = /^(?:checkbox|radio)-group-[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const DATE_PICKER_MIN_YEAR = 1900;
const DATE_PICKER_FUTURE_YEAR_BUFFER = 10;
const DEFAULT_PAGE_PIXEL_WIDTH = 1000;
const DEFAULT_PAGE_PIXEL_HEIGHT = 1400;
const TEXTBOX_HORIZONTAL_PADDING_PX = 6;
const TEXTBOX_VERTICAL_PADDING_PX = 3;
const MIN_AUTO_RESIZE_FONT_SIZE_PX = 8;
const APPROXIMATE_CHARACTER_WIDTH_FACTOR = 0.6;
const DOCUMENT_ID_HEADER_CLASS = 'document-id-page-header';
const DEFAULT_TEXTBOX_FONT_SETTINGS: ResolvedFieldFontSettings = {
  family: 'Helvetica',
  size: 13,
  lineHeight: 15,
  bold: false,
  italic: false,
  underline: false,
  color: '#1b2d3f',
  align: 'left',
  characterSpacing: 0,
  rtl: false,
};
const DEFAULT_TEXTBOX_SETTINGS: ResolvedTextboxSettings = {
  resizeType: 'grow_vertically',
  fixedWidth: false,
  characterLimit: undefined,
  validationType: 'none',
  validationValue: undefined,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
  selector: 'app-sign-request',
  imports: [CommonModule, FormsModule, NgxExtendedPdfViewerModule, ReadOnlyPdfReviewComponent],
  templateUrl: './sign-request.component.html',
  styleUrl: './sign-request.component.scss',
})
export class SignRequestComponent implements OnInit, OnDestroy {
  @ViewChild('viewerHost') private readonly viewerHost?: ElementRef<HTMLElement>;
  @ViewChild('walkthroughRail') private readonly walkthroughRail?: ElementRef<HTMLElement>;
  @ViewChild('drawCanvas') private readonly drawCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('imageStage') private readonly imageStage?: ElementRef<HTMLDivElement>;

  private static readonly CONSENT_STORAGE_PREFIX = 'sign-disclosure-consent:';
  private static readonly WALKTHROUGH_ARROW_HEIGHT_PX = 40;
  private static readonly WALKTHROUGH_DEFAULT_TOP_PX = 48;
  private static readonly WALKTHROUGH_EDGE_PADDING_PX = 10;

  token = '';
  session: SignSessionResponse | null = null;
  loading = false;
  submitting = false;
  declining = false;
  errorMessage = '';
  viewerPage = 1;
  pageCount = 1;
  zoomLevel = 'page-width';
  viewerRotation: 0 | 90 | 180 | 270 = 0;
  isSigningStarted = false;
  activeFieldId: string | null = null;
  walkthroughArrowOffset = SignRequestComponent.WALKTHROUGH_DEFAULT_TOP_PX;

  declineReason = '';
  isDeclineModalOpen = false;
  hasAcceptedDisclosure = false;
  disclosureConsentAcceptedAt: string | null = null;
  hasUnlockedSigningUi = false;
  isDisclosureModalOpen = false;
  consentHintVisible = false;

  isMarkModalOpen = false;
  markFieldId: string | null = null;
  markKind: MarkKind | null = null;
  markTab: MarkTab = 'type';
  markTypeInput = '';
  markTypeStyleIndex = 0;
  markUploadError = '';
  markUploadDataUrl: string | null = null;
  markDrawHasStroke = false;
  lastSignatureDataUrl: string | null = null;
  lastInitialsDataUrl: string | null = null;
  datePickerFieldId: string | null = null;
  datePickerMonth = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1));
  invalidEditableDateFields = new Set<string>();
  invalidTextboxFields = new Set<string>();
  isImageModalOpen = false;
  imageFieldId: string | null = null;
  imageModalError = '';
  imageSourceDataUrl: string | null = null;
  imageDraftDataUrl: string | null = null;
  imageMarkedForRemoval = false;
  imageScale = 1;
  imageOffsetX = 0;
  imageOffsetY = 0;
  private imagePointerState: ImagePointerState | null = null;

  readonly markTabs = MARK_TABS;
  readonly typeStyleOptions = TYPE_STYLE_OPTIONS;

  readonly disclosureTermsSections: Array<{ title?: string; text: string }> = [
    {
      text: 'Consumer disclosure regarding conducting business electronically, receiving electronic notices, disclosures, and other documents, and to electronically sign documents.',
    },
    {
      text: 'Use of the service requires a standards-compliant web browser operating on a computer system with suitable capacity. Viewing PDF documents may require additional software that we do not provide or license.',
    },
    {
      text: 'By proceeding and signing this document, you are agreeing that you have reviewed the disclosure information and consent to transact business using electronic communications and to utilize electronic signatures instead of using paper documents.',
    },
    {
      text: 'You are not required to receive Electronic Documents. If you prefer not to do so, you may request to receive paper copies and withdraw your consent at any time.',
    },
    {
      title: 'Electronic Documents',
      text: 'Electronic documents include disclosures, contracts, information, forms, signature requests, and any other documents sent through this signing service.',
    },
    {
      title: 'Sending Party',
      text: 'The sending party is the customer sending Electronic Documents to you.',
    },
  ];

  pdfUrl: string | null = null;
  fieldValues = new Map<string, unknown>();
  imagePrimarySourceByFieldId = new Map<string, string>();

  private overlaySyncTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingFocusFieldId: string | null = null;
  private isDrawing = false;
  private fieldNodeCache = new Map<string, FieldNodeCacheEntry>();
  private previousDisableHistory = pdfDefaultOptions.disableHistory;
  private textMeasureElement: HTMLDivElement | null = null;

  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(DocumentApiService);
  private readonly feedback = inject(GlobalFeedbackService);
  private readonly dateTimeFormat = inject(UserDateTimeFormatService);
  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.previousDisableHistory = pdfDefaultOptions.disableHistory;
    pdfDefaultOptions.disableHistory = true;
    this.token = this.route.snapshot.paramMap.get('token') ?? '';
    if (!this.token) {
      this.errorMessage = 'Signing token is missing.';
      return;
    }

    this.loadSession();
  }

  ngOnDestroy(): void {
    if (this.overlaySyncTimer) {
      clearTimeout(this.overlaySyncTimer);
      this.overlaySyncTimer = null;
    }

    if (this.pdfUrl) {
      URL.revokeObjectURL(this.pdfUrl);
      this.pdfUrl = null;
    }

    this.clearFieldNodeCache();
    this.textMeasureElement?.remove();
    this.textMeasureElement = null;
    pdfDefaultOptions.disableHistory = this.previousDisableHistory;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.datePickerFieldId) {
      this.closeDatePicker();
      return;
    }

    if (this.isMarkModalOpen) {
      this.closeMarkModal();
      return;
    }

    if (this.isImageModalOpen) {
      this.closeImageModal();
      return;
    }

    if (this.isDeclineModalOpen) {
      this.closeDeclineModal();
      return;
    }

    this.closeDisclosureModal();
  }

  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(event: PointerEvent): void {
    if (!this.datePickerFieldId) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const insidePopover = !!target.closest('.sign-date-popover');
    const insideActiveField = !!target.closest(`.sign-field-chip[data-field-id="${this.datePickerFieldId}"]`);
    if (!insidePopover && !insideActiveField) {
      this.closeDatePicker();
      this.scheduleOverlaySync();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleOverlaySync();
  }

  get supportedFields(): DocumentFieldDraft[] {
    if (!this.session) {
      return [];
    }

    return this.session.fields.filter((field) => SUPPORTED_FIELD_TYPES.includes(field.type));
  }

  get supportedRequiredFields(): DocumentFieldDraft[] {
    return this.supportedFields.filter((field) => field.required);
  }

  get filledRequiredCount(): number {
    return this.requiredFieldUnits().filter((unit) => unit.fields.some((field) => this.isFilled(field))).length;
  }

  get requiredCount(): number {
    return this.requiredFieldUnits().length;
  }

  get requiredLeftCount(): number {
    return Math.max(0, this.requiredCount - this.filledRequiredCount);
  }

  get progressLabel(): string {
    return `${this.filledRequiredCount} of ${this.requiredCount} fields filled`;
  }

  get isCompletedView(): boolean {
    return this.session?.viewMode === 'completed';
  }

  get isDeclinedView(): boolean {
    return this.session?.viewMode === 'declined';
  }

  get isExpiredView(): boolean {
    return this.session?.viewMode === 'expired';
  }

  get isDeletedView(): boolean {
    return this.session?.viewMode === 'deleted';
  }

  get isReadOnlyView(): boolean {
    return this.isCompletedView || this.isDeclinedView || this.isExpiredView || this.isDeletedView;
  }

  get completedAtLabel(): string {
    const completedAt = this.session?.recipient.completedAt;
    if (!completedAt) {
      return 'Completed';
    }

    return this.dateTimeFormat.formatDateTimeWithPreferences(completedAt, this.signSessionPreferences(), {
      includeTimeZoneName: true,
    });
  }

  get declinedAtLabel(): string {
    const declinedAt = this.session?.recipient.declinedAt;
    if (!declinedAt) {
      return 'Declined';
    }

    return this.dateTimeFormat.formatDateTimeWithPreferences(declinedAt, this.signSessionPreferences(), {
      includeTimeZoneName: true,
    });
  }

  get readOnlyReviewEyebrow(): string {
    if (this.isDeclinedView) {
      return 'Request declined';
    }

    if (this.isExpiredView) {
      return 'Request expired';
    }

    if (this.isDeletedView) {
      return 'Document deleted';
    }

    return 'Document signed';
  }

  get readOnlyReviewTitle(): string {
    if (this.isDeclinedView) {
      return 'You declined this signing request';
    }

    if (this.isExpiredView) {
      return 'This signing request has expired';
    }

    if (this.isDeletedView) {
      return 'This document has been deleted';
    }

    return 'You have already signed this document';
  }

  get readOnlyReviewCopy(): string {
    if (this.isDeclinedView) {
      return 'This signing link is now in read-only mode. You can review the document below, but you can no longer sign it.';
    }

    if (this.isExpiredView) {
      return 'This signing link is now in read-only mode because the request expired. You can review the document below, but signing is no longer available.';
    }

    if (this.isDeletedView) {
      return 'This signing link is now in read-only mode because the sender deleted the document. You can review the document below, but no further action is available.';
    }

    return 'This signing link is now in read-only mode. You can review the document and your signing details below.';
  }

  get readOnlyReviewMetaItems(): Array<{ label: string; value: string }> {
    const session = this.session;
    if (!session) {
      return [];
    }

    return [
      { label: 'Document', value: session.document.title },
      { label: 'Signer', value: session.recipient.name },
      { label: 'Email', value: session.recipient.email },
      {
        label: this.isDeclinedView ? 'Declined at' : this.isExpiredView ? 'Expired on' : this.isDeletedView ? 'Status' : 'Signed at',
        value:
          this.isDeclinedView
            ? this.declinedAtLabel
            : this.isExpiredView
              ? this.dateTimeFormat.formatDateTimeWithPreferences(session.document.expiryAt, this.signSessionPreferences(), {
                  includeTimeZoneName: true,
                })
              : this.isDeletedView
                ? 'Deleted by sender'
                : this.completedAtLabel,
      },
    ];
  }

  get readOnlyFieldValues(): ReadonlyMap<string, unknown> {
    return this.fieldValues;
  }

  get readOnlySignatureFrameMetadata(): Record<
    string,
    { name: string; email: string; timestampLabel: string; documentId: string }
  > {
    const session = this.session;
    if (!session) {
      return {};
    }

    const timestampIso = session.recipient.completedAt ?? '';
    const timestampLabel = timestampIso
      ? this.dateTimeFormat.formatDateTimeWithPreferences(timestampIso, this.signSessionPreferences(), {
          includeTimeZoneName: true,
        })
      : '';

    return Object.fromEntries(
      session.fields
        .filter((field) => field.type === 'signature')
        .map((field) => [
          field.id,
          {
            name: session.recipient.name,
            email: session.recipient.email,
            timestampLabel,
            documentId: session.document.id,
          },
        ]),
    );
  }

  get primaryActionLabel(): string {
    if (this.isReadOnlyView) {
      return 'Signed';
    }

    if (!this.hasUnlockedSigningUi) {
      return 'Start signing';
    }

    if (!this.isSigningStarted) {
      return 'Start signing';
    }

    return this.requiredLeftCount === 0 ? 'Finish' : 'Next field';
  }

  get canUsePrimaryAction(): boolean {
    if (this.isReadOnlyView) {
      return false;
    }

    if (!this.hasUnlockedSigningUi || this.submitting) {
      return false;
    }

    if (!this.isSigningStarted) {
      return this.supportedFields.length > 0;
    }

    if (this.requiredLeftCount === 0) {
      return true;
    }

    return this.nextIncompleteRequiredField() !== null;
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.pageCount }, (_, index) => index + 1);
  }

  get calendarHeaderLabel(): string {
    return this.dateTimeFormat.formatMonthYear(this.datePickerLabelDate(this.datePickerMonth));
  }

  get datePickerMonthOptions(): Array<{ value: number; label: string }> {
    return Array.from({ length: 12 }, (_, index) => ({
      value: index,
      label: this.dateTimeFormat.formatMonthName(new Date(Date.UTC(2024, index, 15, 12))),
    }));
  }

  get datePickerYearOptions(): number[] {
    return this.yearOptionsForDatePicker();
  }

  get leftGuideLabel(): string {
    if (!this.isSigningStarted) {
      return 'Start signing';
    }

    if (this.requiredLeftCount === 0) {
      return 'Finish';
    }

    const active = this.activeField();
    if (active?.type === 'editable_date') {
      return 'Select Date';
    }

    if (active?.type === 'signature' || active?.type === 'initials') {
      return 'Sign Here';
    }

    if (active?.type === 'image') {
      return 'Upload Image';
    }

    return 'Fill Value';
  }

  get canSaveMark(): boolean {
    if (!this.markKind) {
      return false;
    }

    if (this.markTab === 'type') {
      return this.markTypeInput.trim().length > 0;
    }

    if (this.markTab === 'draw') {
      return this.markDrawHasStroke;
    }

    return !!this.markUploadDataUrl && !this.markUploadError;
  }

  get canReuseCurrentMark(): boolean {
    if (this.markKind === 'signature') {
      return !!this.lastSignatureDataUrl;
    }

    if (this.markKind === 'initials') {
      return !!this.lastInitialsDataUrl;
    }

    return false;
  }

  get canSaveImage(): boolean {
    return this.imageMarkedForRemoval || (!!this.imageSourceDataUrl && !this.imageModalError);
  }

  get imagePreviewTransform(): string {
    return `translate(calc(-50% + ${this.imageOffsetX}px), calc(-50% + ${this.imageOffsetY}px)) scale(${this.imageScale})`;
  }

  get imageRequestedLabel(): string {
    const active = this.activeField();
    return active?.fieldKey || active?.label || 'image';
  }

  valueForField(field: DocumentFieldDraft): string {
    const value = this.fieldValues.get(field.id);
    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number') {
      return String(value);
    }

    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }

    return '';
  }

  onFieldFocus(field: DocumentFieldDraft): void {
    if (this.isReadOnlyView) {
      return;
    }

    this.activeFieldId = field.id;
    this.scheduleOverlaySync();

    if (field.type === 'editable_date') {
      this.openDatePicker(field.id);
    }
  }

  updateFieldValue(field: DocumentFieldDraft, value: string): void {
    if (this.isReadOnlyView || !this.hasUnlockedSigningUi || !SUPPORTED_FIELD_TYPES.includes(field.type)) {
      return;
    }

    if (field.type === 'email' || field.type === 'date_signed') {
      return;
    }

    this.fieldValues.set(field.id, value);
    if (field.type === 'editable_date') {
      this.syncEditableDateValidity(field.id, value);
    } else if (field.type === 'textbox') {
      this.syncTextboxValidity(field, value);
    }
  }

  isEditableField(field: DocumentFieldDraft): boolean {
    if (this.isReadOnlyView || !this.hasUnlockedSigningUi || !SUPPORTED_FIELD_TYPES.includes(field.type)) {
      return false;
    }

    return (
      field.type !== 'email' &&
      field.type !== 'date_signed' &&
      field.type !== 'signature' &&
      field.type !== 'initials'
    );
  }

  startOrAdvance(): void {
    if (this.isReadOnlyView || !this.hasUnlockedSigningUi || this.submitting) {
      return;
    }

    if (!this.isSigningStarted) {
      this.isSigningStarted = true;
      const first = this.nextIncompleteRequiredField() ?? this.sortedSupportedFields()[0] ?? null;
      if (first) {
        this.focusField(first);
      }
      return;
    }

    if (this.requiredLeftCount === 0) {
      this.submit();
      return;
    }

    const next = this.nextIncompleteRequiredField();
    if (next) {
      this.focusField(next);
    }
  }

  jumpToPage(page: number): void {
    this.viewerPage = this.clamp(page, 1, this.pageCount);
    this.scheduleOverlaySync();
  }

  zoomIn(): void {
    const current = this.zoomAsNumber();
    this.zoomLevel = String(Math.min(2.2, current + 0.1));
    this.scheduleOverlaySync();
  }

  zoomOut(): void {
    const current = this.zoomAsNumber();
    this.zoomLevel = String(Math.max(0.5, current - 0.1));
    this.scheduleOverlaySync();
  }

  onPagesLoaded(event: unknown): void {
    const pagesCount = (event as { pagesCount?: unknown })?.pagesCount;
    if (typeof pagesCount === 'number' && Number.isInteger(pagesCount) && pagesCount > 0) {
      this.pageCount = pagesCount;
      this.viewerPage = this.clamp(this.viewerPage, 1, this.pageCount);
    }

    this.scheduleOverlaySync();
  }

  onPageRendered(): void {
    this.ensureViewerRotationReset();
    this.scheduleOverlaySync();
  }

  onPageChange(event: unknown): void {
    const pageNumber = this.extractPageNumber(event);
    if (pageNumber) {
      this.viewerPage = this.clamp(pageNumber, 1, this.pageCount);
    }

    this.scheduleOverlaySync();
  }

  onViewerRotationChange(rotation: 0 | 90 | 180 | 270): void {
    this.viewerRotation = rotation;
    this.ensureViewerRotationReset();
  }

  openDatePicker(fieldId: string): void {
    if (this.isReadOnlyView) {
      return;
    }

    this.datePickerFieldId = fieldId;
    const field = this.session?.fields.find((entry) => entry.id === fieldId);
    const current = field ? this.valueForField(field).trim() : '';
    const parsed = this.parseDateInput(current);
    const seedDate = parsed ? this.dateTimeFormat.formatDateInput(parsed) : this.dateTimeFormat.formatDateInput(new Date());
    this.datePickerMonth = this.datePickerMonthFromInput(seedDate);
    this.scheduleOverlaySync();
  }

  closeDatePicker(): void {
    this.datePickerFieldId = null;
  }

  isDatePickerOpen(fieldId: string): boolean {
    return this.datePickerFieldId === fieldId;
  }

  goToPreviousMonth(): void {
    this.datePickerMonth = new Date(Date.UTC(this.datePickerMonth.getUTCFullYear(), this.datePickerMonth.getUTCMonth() - 1, 1));
    this.scheduleOverlaySync();
  }

  goToNextMonth(): void {
    this.datePickerMonth = new Date(Date.UTC(this.datePickerMonth.getUTCFullYear(), this.datePickerMonth.getUTCMonth() + 1, 1));
    this.scheduleOverlaySync();
  }

  setDatePickerMonth(month: number): void {
    if (!Number.isInteger(month) || month < 0 || month > 11) {
      return;
    }

    this.datePickerMonth = new Date(Date.UTC(this.datePickerMonth.getUTCFullYear(), month, 1));
    this.scheduleOverlaySync();
  }

  setDatePickerYear(year: number): void {
    if (!Number.isInteger(year)) {
      return;
    }

    this.datePickerMonth = new Date(Date.UTC(year, this.datePickerMonth.getUTCMonth(), 1));
    this.scheduleOverlaySync();
  }

  calendarDays(): Array<{ date: Date; inCurrentMonth: boolean }> {
    const monthStart = new Date(Date.UTC(this.datePickerMonth.getUTCFullYear(), this.datePickerMonth.getUTCMonth(), 1));
    const firstWeekStart = new Date(monthStart);
    firstWeekStart.setUTCDate(monthStart.getUTCDate() - monthStart.getUTCDay());

    const days: Array<{ date: Date; inCurrentMonth: boolean }> = [];
    for (let index = 0; index < 42; index += 1) {
      const value = new Date(firstWeekStart);
      value.setUTCDate(firstWeekStart.getUTCDate() + index);
      days.push({
        date: value,
        inCurrentMonth: value.getUTCMonth() === this.datePickerMonth.getUTCMonth(),
      });
    }

    return days;
  }

  pickDateForField(fieldId: string, date: Date): void {
    const parsed = this.dateTimeFormat.parseDateInput(this.utcDateInputValue(date)) ?? date;
    this.fieldValues.set(fieldId, this.formatDate(parsed));
    this.invalidEditableDateFields.delete(fieldId);
    this.activeFieldId = fieldId;
    this.closeDatePicker();
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  focusField(field: DocumentFieldDraft): void {
    this.viewerPage = this.clamp(field.page, 1, this.pageCount);
    this.activeFieldId = field.id;
    this.pendingFocusFieldId = field.id;
    if (field.type !== 'editable_date') {
      this.closeDatePicker();
    }
    this.scheduleOverlaySync();
  }

  submit(): void {
    if (!this.token || !this.session || this.submitting || !this.hasUnlockedSigningUi || this.isReadOnlyView) {
      return;
    }

    const firstInvalidTextbox = this.firstInvalidTextboxField();
    if (firstInvalidTextbox) {
      this.errorMessage = 'Please correct the highlighted textbox fields before finishing.';
      this.focusField(firstInvalidTextbox);
      this.cdr.markForCheck();
      return;
    }

    const values = this.session.fields.map((field) => ({
      fieldId: field.id,
      value: this.fieldValues.get(field.id) ?? null,
    }));

    this.submitting = true;
    this.api
      .submitSign(this.token, values, this.disclosureConsentAcceptedAt ?? undefined)
      .pipe(
        finalize(() => {
          this.submitting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.feedback.clearError();
          this.loadSession();
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: 'Failed to submit signing response',
          });
          this.errorMessage = normalized.message;
          this.feedback.showError({ ...normalized });
          this.cdr.markForCheck();
        },
      });
  }

  decline(): void {
    if (this.isReadOnlyView) {
      return;
    }

    if (!this.token || this.declining) {
      return;
    }

    this.declining = true;
    this.api
      .declineSign(this.token, this.declineReason.trim() || undefined)
      .pipe(
        finalize(() => {
          this.declining = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.feedback.clearError();
          this.closeDeclineModal();
          this.loadSession();
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: 'Failed to decline signing request',
          });
          this.errorMessage = normalized.message;
          this.feedback.showError({ ...normalized });
          this.cdr.markForCheck();
        },
      });
  }

  openMarkModal(field: DocumentFieldDraft): void {
    if (!this.hasUnlockedSigningUi || this.isReadOnlyView) {
      return;
    }

    if (field.type !== 'signature' && field.type !== 'initials') {
      return;
    }

    this.markFieldId = field.id;
    this.markKind = field.type;
    this.markTab = 'type';
    this.markUploadError = '';
    this.markUploadDataUrl = null;
    this.markDrawHasStroke = false;
    this.isMarkModalOpen = true;

    const existing = this.valueForField(field).trim();
    if (existing && !existing.startsWith('data:image')) {
      this.markTypeInput = existing;
    } else {
      this.markTypeInput = field.type === 'initials' ? this.defaultInitials() : this.defaultSignerName();
    }

    setTimeout(() => this.prepareDrawCanvas(), 0);
  }

  closeMarkModal(): void {
    this.isMarkModalOpen = false;
    this.markFieldId = null;
    this.markKind = null;
    this.markUploadError = '';
    this.markUploadDataUrl = null;
    this.markDrawHasStroke = false;
  }

  openImageModal(field: DocumentFieldDraft): void {
    if (!this.hasUnlockedSigningUi || this.isReadOnlyView || field.type !== 'image') {
      return;
    }

    this.imageFieldId = field.id;
    this.activeFieldId = field.id;
    this.scheduleOverlaySync();
    this.imageModalError = '';
    this.imageScale = 1;
    this.imageOffsetX = 0;
    this.imageOffsetY = 0;
    this.imagePointerState = null;
    const primary = this.imagePrimarySourceByFieldId.get(field.id) ?? null;
    if (primary) {
      this.imageSourceDataUrl = primary;
    } else {
      const existing = this.valueForField(field).trim();
      this.imageSourceDataUrl = existing.startsWith('data:image') ? existing : null;
      if (this.imageSourceDataUrl) {
        // Backward-compatible fallback for sessions that predate primary-source caching.
        this.imagePrimarySourceByFieldId.set(field.id, this.imageSourceDataUrl);
      }
    }
    this.imageDraftDataUrl = this.imageSourceDataUrl;
    this.imageMarkedForRemoval = false;
    this.isImageModalOpen = true;
    this.cdr.markForCheck();
  }

  closeImageModal(): void {
    this.isImageModalOpen = false;
    this.imageFieldId = null;
    this.imageModalError = '';
    this.imageSourceDataUrl = null;
    this.imageDraftDataUrl = null;
    this.imageMarkedForRemoval = false;
    this.imageScale = 1;
    this.imageOffsetX = 0;
    this.imageOffsetY = 0;
    this.imagePointerState = null;
  }

  onImageUploadInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0);
    if (!file) {
      return;
    }

    this.processImageUpload(file);
    input.value = '';
  }

  onImageUploadDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.item(0);
    if (!file) {
      return;
    }

    this.processImageUpload(file);
  }

  onImageUploadDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onImageScaleChange(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    if (!Number.isFinite(value)) {
      return;
    }

    this.imageScale = this.clamp(value, 0.3, 3);
  }

  increaseImageScale(): void {
    this.imageScale = this.clamp(this.imageScale + 0.1, 0.3, 3);
  }

  resetImageTransform(): void {
    this.imageScale = 1;
    this.imageOffsetX = 0;
    this.imageOffsetY = 0;
  }

  removeImageSelection(): void {
    this.imageSourceDataUrl = null;
    this.imageDraftDataUrl = null;
    this.imageMarkedForRemoval = true;
    this.imageModalError = '';
    this.resetImageTransform();
  }

  onImageStagePointerDown(event: PointerEvent): void {
    if (!this.imageSourceDataUrl || !this.imageStage?.nativeElement) {
      return;
    }

    event.preventDefault();
    const stage = this.imageStage.nativeElement;
    stage.setPointerCapture(event.pointerId);
    this.imagePointerState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      baseX: this.imageOffsetX,
      baseY: this.imageOffsetY,
    };
  }

  onImageStagePointerMove(event: PointerEvent): void {
    if (!this.imagePointerState || this.imagePointerState.pointerId !== event.pointerId) {
      return;
    }

    this.imageOffsetX = this.imagePointerState.baseX + (event.clientX - this.imagePointerState.startX);
    this.imageOffsetY = this.imagePointerState.baseY + (event.clientY - this.imagePointerState.startY);
  }

  onImageStagePointerUp(event: PointerEvent): void {
    if (!this.imagePointerState || this.imagePointerState.pointerId !== event.pointerId) {
      return;
    }

    this.imageStage?.nativeElement.releasePointerCapture(event.pointerId);
    this.imagePointerState = null;
  }

  async saveImageAndUse(): Promise<void> {
    if (!this.imageFieldId) {
      return;
    }

    if (!this.imageSourceDataUrl) {
      if (this.imageMarkedForRemoval) {
        this.fieldValues.set(this.imageFieldId, null);
        this.imagePrimarySourceByFieldId.delete(this.imageFieldId);
        this.closeImageModal();
        this.scheduleOverlaySync();
        this.cdr.markForCheck();
      }
      return;
    }

    const rendered = await this.renderImageFieldDataUrl();
    if (!rendered) {
      this.imageModalError = 'Unable to render image.';
      this.cdr.markForCheck();
      return;
    }

    this.fieldValues.set(this.imageFieldId, rendered);
    this.closeImageModal();
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  selectMarkTab(tab: MarkTab): void {
    this.markTab = tab;
    if (tab === 'draw') {
      setTimeout(() => this.prepareDrawCanvas(), 0);
    }
  }

  selectTypeStyle(index: number): void {
    this.markTypeStyleIndex = this.clamp(index, 0, this.typeStyleOptions.length - 1);
  }

  onMarkUploadInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0);
    if (!file) {
      return;
    }

    this.processMarkUpload(file);
    input.value = '';
  }

  onMarkUploadDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.item(0);
    if (!file) {
      return;
    }

    this.processMarkUpload(file);
  }

  onMarkUploadDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  clearDrawCanvas(): void {
    const canvas = this.drawCanvas?.nativeElement;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.markDrawHasStroke = false;
  }

  onDrawPointerDown(event: PointerEvent): void {
    const canvas = this.drawCanvas?.nativeElement;
    if (!canvas || this.markTab !== 'draw') {
      return;
    }

    canvas.setPointerCapture(event.pointerId);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    this.isDrawing = true;
    const { x, y } = this.pointerPosition(canvas, event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  onDrawPointerMove(event: PointerEvent): void {
    if (!this.isDrawing) {
      return;
    }

    const canvas = this.drawCanvas?.nativeElement;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const { x, y } = this.pointerPosition(canvas, event);
    ctx.lineTo(x, y);
    ctx.stroke();
    this.markDrawHasStroke = true;
  }

  onDrawPointerUp(event: PointerEvent): void {
    const canvas = this.drawCanvas?.nativeElement;
    if (canvas) {
      canvas.releasePointerCapture(event.pointerId);
    }

    this.isDrawing = false;
  }

  async saveMarkAndUse(): Promise<void> {
    if (!this.markFieldId || !this.markKind || !this.canSaveMark) {
      return;
    }

    let dataUrl: string | null = null;
    if (this.markTab === 'type') {
      const style = this.selectedTypeStyle;
      await this.ensureTypedMarkFontLoaded(style);
      dataUrl = this.makeTypedMarkDataUrl(this.markTypeInput.trim(), style);
    } else if (this.markTab === 'draw') {
      dataUrl = this.drawCanvas?.nativeElement.toDataURL('image/png') || null;
    } else if (this.markTab === 'upload') {
      dataUrl = this.markUploadDataUrl;
    }

    if (!dataUrl) {
      return;
    }

    const normalized = await this.normalizeMarkDataUrl(dataUrl);
    if (!normalized) {
      return;
    }

    this.fieldValues.set(this.markFieldId, normalized);
    if (this.markKind === 'signature') {
      this.lastSignatureDataUrl = normalized;
    } else {
      this.lastInitialsDataUrl = normalized;
    }

    this.closeMarkModal();
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  reuseSavedMark(): void {
    if (!this.markFieldId || !this.markKind) {
      return;
    }

    const reusable = this.markKind === 'signature' ? this.lastSignatureDataUrl : this.lastInitialsDataUrl;
    if (!reusable) {
      return;
    }

    this.fieldValues.set(this.markFieldId, reusable);
    this.closeMarkModal();
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  openDisclosureModal(): void {
    this.isDisclosureModalOpen = true;
  }

  closeDisclosureModal(): void {
    this.isDisclosureModalOpen = false;
  }

  openDeclineModal(): void {
    if (this.isReadOnlyView || this.declining) {
      return;
    }

    this.isDeclineModalOpen = true;
  }

  closeDeclineModal(): void {
    this.isDeclineModalOpen = false;
    this.declineReason = '';
  }

  onDisclosureAcceptanceChange(): void {
    if (this.hasAcceptedDisclosure) {
      this.disclosureConsentAcceptedAt ??= new Date().toISOString();
      this.consentHintVisible = false;
    } else {
      this.disclosureConsentAcceptedAt = null;
    }
  }

  continueSigning(): void {
    if (this.isReadOnlyView) {
      return;
    }

    if (!this.hasAcceptedDisclosure) {
      this.consentHintVisible = true;
      return;
    }

    if (!this.hasUnlockedSigningUi) {
      this.hasUnlockedSigningUi = true;
      this.consentHintVisible = false;
      this.disclosureConsentAcceptedAt ??= new Date().toISOString();
      this.persistConsentState();
      this.scheduleOverlaySync();
      return;
    }

    this.startOrAdvance();
  }

  private loadSession(): void {
    if (!this.token) {
      return;
    }

    this.loading = true;
    this.api
      .getSignSession(this.token)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (session) => {
          this.session = session;
          this.errorMessage = '';
          const consentAlreadyGiven = this.readConsentState();
          const isReadOnlyView = session.viewMode !== 'active';
          this.hasAcceptedDisclosure = isReadOnlyView ? true : consentAlreadyGiven;
          this.hasUnlockedSigningUi = isReadOnlyView ? true : consentAlreadyGiven;
          this.disclosureConsentAcceptedAt = this.hasAcceptedDisclosure ? new Date().toISOString() : null;
          this.isDisclosureModalOpen = false;
          this.isDeclineModalOpen = false;
          this.consentHintVisible = false;
          this.invalidEditableDateFields.clear();
          this.invalidTextboxFields.clear();
          this.isSigningStarted = false;
          this.activeFieldId = null;
          this.pendingFocusFieldId = null;
          this.declineReason = '';
          this.closeDatePicker();
          this.closeMarkModal();
          this.closeImageModal();
          this.viewerPage = 1;
          this.pageCount = session.document.sourceFile.pageCount || 1;
          this.fieldValues.clear();
          this.imagePrimarySourceByFieldId.clear();
          this.clearFieldNodeCache();
          for (const value of session.values) {
            this.fieldValues.set(value.fieldId, value.value);
          }

          if (session.viewMode === 'active') {
            this.applyPrefillDefaults(session);
            this.syncAllTextboxValidity(session.fields);
          }
          this.loadPdfBlob();
          this.scheduleOverlaySync();
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: 'Unable to open signing request',
          });
          this.errorMessage = normalized.message;
          this.feedback.showError({ ...normalized });
          this.cdr.markForCheck();
        },
      });
  }

  private loadPdfBlob(): void {
    if (!this.token) {
      return;
    }

    this.viewerRotation = 0;

    if (this.isReadOnlyView && this.pdfUrl) {
      URL.revokeObjectURL(this.pdfUrl);
      this.pdfUrl = null;
    }

    this.api.getSignPdf(this.token).subscribe({
      next: (blob) => {
        if (this.pdfUrl) {
          URL.revokeObjectURL(this.pdfUrl);
        }

        this.viewerRotation = 0;
        this.pdfUrl = URL.createObjectURL(blob);
        this.scheduleOverlaySync();
        this.cdr.markForCheck();
      },
      error: () => {
        this.pdfUrl = null;
        this.cdr.markForCheck();
      },
    });
  }

  private ensureViewerRotationReset(): void {
    if (this.viewerRotation === 0) {
      return;
    }

    this.viewerRotation = 0;
    this.cdr.markForCheck();
    this.scheduleOverlaySync();
  }

  private scheduleOverlaySync(): void {
    if (this.overlaySyncTimer) {
      clearTimeout(this.overlaySyncTimer);
    }

    this.overlaySyncTimer = setTimeout(() => this.syncOverlays(), 0);
  }

  private syncOverlays(): void {
    const host = this.viewerHost?.nativeElement;
    if (!host) {
      return;
    }

    this.syncWalkthroughArrow();
    if (!this.session) {
      return;
    }

    const pages = Array.from(host.querySelectorAll<HTMLElement>('.page[data-page-number]'));
    const renderedFieldIds = new Set<string>();
    for (const pageElement of pages) {
      pageElement.style.position = 'relative';
      const pageNumber = Number(pageElement.dataset['pageNumber']);
      if (!Number.isInteger(pageNumber)) {
        continue;
      }

      const layer = this.ensureOverlayLayer(pageElement);
      this.syncDocumentHeader(layer);
      const pageFields = this.session.fields
        .filter((field) => field.page === pageNumber)
        .sort((a, b) => {
          if (a.rect.y !== b.rect.y) {
            return a.rect.y - b.rect.y;
          }

          return a.rect.x - b.rect.x;
        });

      for (const field of pageFields) {
        const node = this.getOrCreateFieldNode(field);
        this.updateFieldNode(field, node);
        if (node.parentElement !== layer) {
          layer.append(node);
        }
        renderedFieldIds.add(field.id);
      }
    }

    this.removeStaleFieldNodes(renderedFieldIds);

    if (this.pendingFocusFieldId) {
      const target = host.querySelector<HTMLInputElement | HTMLTextAreaElement>(
        `.sign-field-chip[data-field-id="${this.pendingFocusFieldId}"] input, .sign-field-chip[data-field-id="${this.pendingFocusFieldId}"] textarea`,
      );
      if (target) {
        target.focus();
        this.pendingFocusFieldId = null;
      }
    }

    this.syncWalkthroughArrow();
  }

  private ensureOverlayLayer(pageElement: HTMLElement): HTMLElement {
    const existing = pageElement.querySelector<HTMLElement>(':scope > .sign-overlay-layer');
    if (existing) {
      return existing;
    }

    const layer = document.createElement('div');
    layer.className = 'sign-overlay-layer';
    pageElement.append(layer);
    return layer;
  }

  private syncDocumentHeader(layer: HTMLElement): void {
    const existing = layer.querySelector<HTMLElement>(`:scope > .${DOCUMENT_ID_HEADER_CLASS}`);
    const headerText = this.documentHeaderText();

    if (!headerText) {
      existing?.remove();
      return;
    }

    const header = existing ?? document.createElement('p');
    header.className = DOCUMENT_ID_HEADER_CLASS;
    header.textContent = headerText;
    if (!existing) {
      layer.prepend(header);
    }
  }

  private documentHeaderText(): string {
    const normalized = this.session?.document.id?.trim() ?? '';
    return normalized ? `Document ID: ${normalized}` : '';
  }

  private getOrCreateFieldNode(field: DocumentFieldDraft): HTMLElement {
    const kind = this.fieldNodeKind(field);
    const cached = this.fieldNodeCache.get(field.id);
    if (cached && cached.page === field.page && cached.kind === kind) {
      return cached.element;
    }

    cached?.element.remove();
    const element = this.createFieldNode(field, kind);
    this.fieldNodeCache.set(field.id, { page: field.page, element, kind });
    return element;
  }

  private createFieldNode(field: DocumentFieldDraft, kind: FieldNodeKind): HTMLElement {
    const element = document.createElement('div');
    element.className = 'sign-field-chip';
    element.dataset['fieldId'] = field.id;
    element.dataset['fieldKind'] = kind;

    if (kind === 'deferred') {
      const deferred = document.createElement('p');
      deferred.className = 'deferred-text';
      deferred.textContent = 'Coming next';
      element.append(deferred);
      return element;
    }

    if (kind === 'mark') {
      element.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.zone.run(() => {
          if (!this.hasUnlockedSigningUi) {
            return;
          }

          this.activeFieldId = field.id;
          this.closeDatePicker();
          this.openMarkModal(field);
          this.scheduleOverlaySync();
          this.cdr.markForCheck();
        });
      });

      return element;
    }

    if (kind === 'image') {
      element.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.zone.run(() => this.openImageModal(field));
      });
      return element;
    }

    if (kind === 'editable_date') {
      const wrap = document.createElement('div');
      wrap.className = 'editable-date-wrap';

      const input = document.createElement('input');
      input.type = 'text';
      input.value = this.valueForField(field);
      input.disabled = !this.hasUnlockedSigningUi;
      input.placeholder = this.emptyFieldPrompt(field, field.placeholder || 'MM/dd/yyyy');
      input.className = this.invalidEditableDateFields.has(field.id) ? 'has-error' : '';

      input.addEventListener('focus', () => {
        this.zone.run(() => {
          this.onFieldFocus(field);
        });
      });

      input.addEventListener('blur', () => {
        this.zone.run(() => {
          this.syncEditableDateValidity(field.id, input.value);
          this.cdr.markForCheck();
        });
      });

      input.addEventListener('input', (event) => {
        this.zone.run(() => {
          const value = (event.target as HTMLInputElement).value;
          this.updateFieldValue(field, value);
          this.cdr.markForCheck();
        });
      });

      wrap.append(input);
      element.append(wrap);

      if (this.isDatePickerOpen(field.id)) {
        element.append(this.buildDatePopover(field.id));
      }

      element.addEventListener('click', () => {
        this.zone.run(() => {
          this.focusField(field);
          this.openDatePicker(field.id);
          setTimeout(() => input.focus(), 0);
        });
      });
      return element;
    }

    if (kind === 'choice') {
      const choiceMark = document.createElement('span');
      choiceMark.className = 'choice-mark';
      element.append(choiceMark);

      element.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.zone.run(() => {
          if (!this.hasUnlockedSigningUi) {
            return;
          }

          this.activeFieldId = field.id;
          this.closeDatePicker();
          if (field.type === 'checkbox') {
            this.fieldValues.set(field.id, !this.isChoiceSelected(this.fieldValues.get(field.id)));
          } else {
            const group = this.radioGroupSnapshot(field);
            for (const member of group.fields) {
              this.fieldValues.set(member.id, member.id === field.id);
            }
          }
          this.scheduleOverlaySync();
          this.cdr.markForCheck();
        });
      });
      return element;
    }

    if (kind === 'dropdown') {
      const select = document.createElement('select');
      select.className = 'sign-dropdown-select';
      select.addEventListener('focus', () => {
        this.zone.run(() => {
          this.onFieldFocus(field);
          this.cdr.markForCheck();
        });
      });

      select.addEventListener('change', (event) => {
        this.zone.run(() => {
          const value = (event.target as HTMLSelectElement).value;
          this.fieldValues.set(field.id, this.dropdownNormalizedValue(field, value) ?? '');
          this.cdr.markForCheck();
        });
      });

      element.addEventListener('click', (event) => {
        event.stopPropagation();
        this.zone.run(() => {
          if (!this.hasUnlockedSigningUi) {
            return;
          }

          this.activeFieldId = field.id;
          this.closeDatePicker();
          this.scheduleOverlaySync();
          select.focus();
          this.cdr.markForCheck();
        });
      });

      element.append(select);
      return element;
    }

    if (field.type === 'textbox') {
      const textarea = document.createElement('textarea');
      textarea.className = 'sign-textarea';
      textarea.value = this.valueForField(field);
      textarea.readOnly = !this.isEditableField(field);
      textarea.disabled = !this.hasUnlockedSigningUi;
      textarea.placeholder = this.emptyFieldPrompt(field, field.placeholder || '');
      textarea.rows = 1;

      textarea.addEventListener('focus', () => {
        this.zone.run(() => {
          this.onFieldFocus(field);
          this.cdr.markForCheck();
        });
      });

      textarea.addEventListener('input', (event) => {
        this.zone.run(() => {
          const nextValue = (event.target as HTMLTextAreaElement).value;
          this.updateFieldValue(field, nextValue);
          this.scheduleOverlaySync();
          this.cdr.markForCheck();
        });
      });

      textarea.addEventListener('blur', (event) => {
        this.zone.run(() => {
          this.syncTextboxValidity(field, (event.target as HTMLTextAreaElement).value);
          this.cdr.markForCheck();
        });
      });

      element.addEventListener('click', () => {
        this.zone.run(() => {
          if (!this.hasUnlockedSigningUi) {
            return;
          }

          this.activeFieldId = field.id;
          this.closeDatePicker();
          this.scheduleOverlaySync();
          textarea.focus();
          this.cdr.markForCheck();
        });
      });

      element.append(textarea);
      return element;
    }

    const input = document.createElement('input');
    input.type = 'text';
    input.value = this.valueForField(field);
    input.readOnly = !this.isEditableField(field);
    input.disabled = !this.hasUnlockedSigningUi;
    input.placeholder = this.emptyFieldPrompt(field, field.placeholder || '');

    input.addEventListener('focus', () => {
      this.zone.run(() => {
        this.onFieldFocus(field);
        this.cdr.markForCheck();
      });
    });

    input.addEventListener('input', (event) => {
      this.zone.run(() => {
        const value = (event.target as HTMLInputElement).value;
        this.updateFieldValue(field, value);
        this.cdr.markForCheck();
      });
    });

    element.addEventListener('click', () => {
      this.zone.run(() => {
        if (!this.hasUnlockedSigningUi) {
          return;
        }

        this.activeFieldId = field.id;
        this.closeDatePicker();
        this.scheduleOverlaySync();
        input.focus();
        this.cdr.markForCheck();
      });
    });

    element.append(input);
    return element;
  }

  private updateFieldNode(field: DocumentFieldDraft, element: HTMLElement): void {
    element.className = 'sign-field-chip';
    element.dataset['fieldId'] = field.id;
    element.dataset['fieldKind'] = this.fieldNodeKind(field);
    element.classList.toggle('is-readonly', this.isReadOnlyView);

    if (!SUPPORTED_FIELD_TYPES.includes(field.type)) {
      element.classList.add('is-deferred');
    } else if (field.id === this.activeFieldId) {
      element.classList.add('is-active');
    } else if (this.isFilled(field)) {
      element.classList.add('is-filled');
    }
    if (this.isFilled(field)) {
      element.classList.add('has-display-value');
    }

    this.applyFieldRect(element, field.rect);

    if (!SUPPORTED_FIELD_TYPES.includes(field.type)) {
      return;
    }

    if (field.type === 'signature' || field.type === 'initials') {
      this.updateMarkFieldNode(field, element);
      return;
    }

    if (field.type === 'image') {
      this.updateImageFieldNode(field, element);
      return;
    }

    if (field.type === 'editable_date') {
      element.classList.add('is-editable-date');
      this.updateEditableDateFieldNode(field, element);
      return;
    }

    if (field.type === 'checkbox' || field.type === 'radio') {
      this.updateChoiceFieldNode(field, element);
      return;
    }

    if (field.type === 'dropdown') {
      element.classList.add('is-dropdown');
      this.updateDropdownFieldNode(field, element);
      return;
    }

    this.updateTextFieldNode(field, element);

    if (field.type === 'textbox') {
      element.classList.add('is-textbox');
      const fontSettings = this.applyTextboxFontStyles(element, field);
      this.applyTextboxResizeBehavior(field, element, fontSettings);
      return;
    }

    if (field.type === 'name' || field.type === 'email') {
      this.applyIdentityFieldResizeBehavior(field, element);
    }
  }

  private updateMarkFieldNode(field: DocumentFieldDraft, element: HTMLElement): void {
    const current = this.valueForField(field).trim();
    const image = element.querySelector<HTMLImageElement>(':scope > .sign-image-preview');
    const promptInput = element.querySelector<HTMLInputElement>(':scope > input[aria-hidden="true"]');
    const existingFrame = element.querySelector<HTMLElement>(':scope > .signature-frame-chip');
    const shouldRenderSignatureFrame = field.type === 'signature' && this.signatureFrameSettings.enabled;

    if (shouldRenderSignatureFrame) {
      promptInput?.remove();
      image?.remove();
      element.classList.add('has-signature-frame-layout');
      this.renderSignatureFrameChip({
        host: element,
        imageDataUrl: current.startsWith('data:image') ? current : undefined,
        emptyPrompt: this.emptyFieldPrompt(field),
        metadata: {
          name: this.session?.recipient.name ?? '',
          email: this.session?.recipient.email ?? '',
          timestampIso: this.session?.recipient.completedAt ?? new Date().toISOString(),
          documentId: this.session?.document.id ?? '',
        },
      });
      return;
    }

    if (current.startsWith('data:image')) {
      element.classList.remove('has-signature-frame-layout');
      existingFrame?.remove();
      if (promptInput) {
        promptInput.remove();
      }

      const preview = image ?? document.createElement('img');
      preview.src = current;
      preview.alt = `${field.type} value`;
      preview.className = 'sign-image-preview';
      if (!image) {
        element.append(preview);
      }
      return;
    }

    element.classList.remove('has-signature-frame-layout');
    existingFrame?.remove();
    if (image) {
      image.remove();
    }

    const input = promptInput ?? document.createElement('input');
    input.type = 'text';
    input.value = '';
    input.readOnly = true;
    input.tabIndex = -1;
    input.placeholder = this.emptyFieldPrompt(field);
    input.setAttribute('aria-hidden', 'true');
    if (!promptInput) {
      element.append(input);
    }
  }

  get signatureFrameSettings(): BrandSignatureFrameSettings {
    return this.session?.brandSignatureFrame ?? DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS;
  }

  private renderSignatureFrameChip(input: {
    host: HTMLElement;
    imageDataUrl?: string;
    emptyPrompt?: string;
    metadata: {
      name: string;
      email: string;
      timestampIso: string;
      documentId: string;
    };
  }): void {
    const settings = this.signatureFrameSettings;
    const existing = input.host.querySelector<HTMLElement>(':scope > .signature-frame-chip');
    const frame = existing ?? document.createElement('div');
    frame.className = 'signature-frame-chip';

    if (!existing) {
      input.host.append(frame);
    }

    frame.replaceChildren();

    const header = document.createElement('div');
    header.className = 'signature-frame-chip__header';
    header.textContent = settings.showTimestamp
      ? `XlhireEsigns · ${this.dateTimeFormat.formatDateTime(input.metadata.timestampIso)}`
      : 'XlhireEsigns';
    frame.append(header);

    const box = document.createElement('div');
    box.className = 'signature-frame-chip__box';
    const body = document.createElement('div');
    body.className = 'signature-frame-chip__box-body';
    if (input.imageDataUrl) {
      const signature = document.createElement('img');
      signature.src = input.imageDataUrl;
      signature.alt = 'signature value';
      signature.className = 'signature-frame-chip__image';
      body.append(signature);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'signature-frame-chip__placeholder';
      placeholder.textContent = input.emptyPrompt || 'Sign here';
      body.append(placeholder);
    }
    box.append(body);
    frame.append(box);

    const footer = document.createElement('div');
    footer.className = 'signature-frame-chip__footer';

    if (settings.showRecipientName) {
      const name = document.createElement('div');
      name.className = 'signature-frame-chip__meta';
      name.textContent = input.metadata.name;
      footer.append(name);
    }

    if (settings.showRecipientEmail) {
      const email = document.createElement('div');
      email.className = 'signature-frame-chip__meta';
      email.textContent = input.metadata.email;
      footer.append(email);
    }

    const documentId = document.createElement('div');
    documentId.className = 'signature-frame-chip__document-id';
    documentId.textContent = input.metadata.documentId;
    footer.append(documentId);
    frame.append(footer);
  }

  private updateImageFieldNode(field: DocumentFieldDraft, element: HTMLElement): void {
    element.classList.add('is-image');
    const current = this.valueForField(field).trim();
    const image = element.querySelector<HTMLImageElement>(':scope > .field-image-preview');
    const placeholder = element.querySelector<HTMLElement>(':scope > .image-placeholder');

    if (current.startsWith('data:image')) {
      if (placeholder) {
        placeholder.remove();
      }

      const preview = image ?? document.createElement('img');
      preview.src = current;
      preview.alt = 'Image value';
      preview.className = 'field-image-preview';
      if (!image) {
        element.append(preview);
      }
      return;
    }

    if (image) {
      image.remove();
    }

    const prompt = placeholder ?? document.createElement('p');
    prompt.className = 'image-placeholder';
    prompt.textContent = this.emptyFieldPrompt(field);
    if (!placeholder) {
      element.append(prompt);
    }
  }

  private updateEditableDateFieldNode(field: DocumentFieldDraft, element: HTMLElement): void {
    const input = element.querySelector<HTMLInputElement>('.editable-date-wrap input');
    if (!input) {
      return;
    }

    this.syncTextControlElementValue(input, this.valueForField(field));
    input.disabled = !this.hasUnlockedSigningUi;
    input.placeholder = this.emptyFieldPrompt(field, field.placeholder || 'MM/dd/yyyy');
    input.className = this.invalidEditableDateFields.has(field.id) ? 'has-error' : '';

    const existingPopover = element.querySelector<HTMLElement>(':scope > .sign-date-popover');
    if (this.isReadOnlyView) {
      existingPopover?.remove();
      return;
    }

    if (this.isDatePickerOpen(field.id)) {
      const nextPopover = this.buildDatePopover(field.id);
      if (existingPopover) {
        existingPopover.replaceWith(nextPopover);
      } else {
        element.append(nextPopover);
      }
    } else {
      existingPopover?.remove();
    }
  }

  private updateChoiceFieldNode(field: DocumentFieldDraft, element: HTMLElement): void {
    element.classList.add(field.type === 'checkbox' ? 'is-checkbox' : 'is-radio');
    const controlSize = field.type === 'checkbox' ? this.getCheckboxPixelSize(field) : this.getRadioPixelSize(field);
    if (controlSize !== null) {
      element.style.width = `${controlSize}px`;
      element.style.height = `${controlSize}px`;
    }

    const selected = this.isChoiceSelected(this.fieldValues.get(field.id));
    element.classList.toggle('is-selected-choice', selected);

    const choiceMark = element.querySelector<HTMLElement>(':scope > .choice-mark');
    if (!choiceMark) {
      return;
    }

    if (controlSize !== null) {
      choiceMark.style.width = '100%';
      choiceMark.style.height = '100%';
    } else {
      choiceMark.style.removeProperty('width');
      choiceMark.style.removeProperty('height');
    }
    choiceMark.classList.toggle('selected', selected);
  }

  private updateDropdownFieldNode(field: DocumentFieldDraft, element: HTMLElement): void {
    const select = element.querySelector<HTMLSelectElement>(':scope > .sign-dropdown-select');
    if (!select) {
      return;
    }

    select.disabled = !this.hasUnlockedSigningUi;
    const options = this.dropdownOptions(field);
    const hint = this.emptyFieldPrompt(field, this.dropdownHint(field));
    const selectedValue = this.dropdownNormalizedValue(field, this.fieldValues.get(field.id)) ?? '';
    const existingOptions = Array.from(select.options).map((option) => ({ value: option.value, label: option.textContent ?? '' }));
    const nextOptions = [{ value: '', label: hint }, ...options.map((optionValue) => ({ value: optionValue, label: optionValue }))];

    const optionsChanged =
      existingOptions.length !== nextOptions.length ||
      existingOptions.some((option, index) => option.value !== nextOptions[index]?.value || option.label !== nextOptions[index]?.label);

    if (optionsChanged) {
      select.replaceChildren();
      for (const optionConfig of nextOptions) {
        const option = document.createElement('option');
        option.value = optionConfig.value;
        option.textContent = optionConfig.label;
        select.append(option);
      }
    }

    if (select.value !== selectedValue) {
      select.value = selectedValue;
    }
  }

  private updateTextFieldNode(field: DocumentFieldDraft, element: HTMLElement): void {
    if (field.type === 'textbox') {
      const textarea = element.querySelector<HTMLTextAreaElement>(':scope > textarea');
      if (!textarea) {
        return;
      }

      const textboxSettings = this.resolveTextboxSettings(field);
      this.syncTextControlElementValue(textarea, this.valueForField(field));
      textarea.readOnly = !this.isEditableField(field);
      textarea.disabled = !this.hasUnlockedSigningUi;
      textarea.placeholder = this.emptyFieldPrompt(field, field.placeholder || '');
      textarea.className = this.invalidTextboxFields.has(field.id) ? 'sign-textarea has-error' : 'sign-textarea';
      if (textboxSettings.characterLimit && textboxSettings.characterLimit > 0) {
        textarea.maxLength = textboxSettings.characterLimit;
      } else {
        textarea.removeAttribute('maxlength');
      }
      return;
    }

    const input = element.querySelector<HTMLInputElement>(':scope > input');
    if (!input) {
      return;
    }

    this.syncTextControlElementValue(input, this.valueForField(field));
    input.readOnly = !this.isEditableField(field);
    input.disabled = !this.hasUnlockedSigningUi;
    input.placeholder = this.emptyFieldPrompt(field, field.placeholder || '');
  }

  private syncTextControlElementValue(input: HTMLInputElement | HTMLTextAreaElement, nextValue: string): void {
    if (input.value === nextValue) {
      return;
    }

    if (document.activeElement === input) {
      return;
    }

    input.value = nextValue;
  }

  private applyTextboxFontStyles(element: HTMLElement, field: DocumentFieldDraft): ResolvedFieldFontSettings {
    const fontSettings = this.resolveTextboxFontSettings(field);
    const textarea = element.querySelector<HTMLTextAreaElement>(':scope > textarea');
    const letterSpacing = `${fontSettings.characterSpacing}px`;

    element.style.fontFamily = fontSettings.family;
    element.style.fontSize = `${fontSettings.size}px`;
    element.style.lineHeight = `${fontSettings.lineHeight}px`;
    element.style.fontWeight = fontSettings.bold ? '700' : '500';
    element.style.fontStyle = fontSettings.italic ? 'italic' : 'normal';
    element.style.textDecoration = fontSettings.underline ? 'underline' : 'none';
    element.style.color = fontSettings.color;
    element.style.textAlign = fontSettings.align;
    element.style.letterSpacing = letterSpacing;
    element.style.direction = fontSettings.rtl ? 'rtl' : 'ltr';

    if (textarea) {
      textarea.style.fontFamily = fontSettings.family;
      textarea.style.fontSize = `${fontSettings.size}px`;
      textarea.style.lineHeight = `${fontSettings.lineHeight}px`;
      textarea.style.fontWeight = fontSettings.bold ? '700' : '500';
      textarea.style.fontStyle = fontSettings.italic ? 'italic' : 'normal';
      textarea.style.textDecoration = fontSettings.underline ? 'underline' : 'none';
      textarea.style.color = fontSettings.color;
      textarea.style.textAlign = fontSettings.align;
      textarea.style.letterSpacing = letterSpacing;
      textarea.style.direction = fontSettings.rtl ? 'rtl' : 'ltr';
    }

    return fontSettings;
  }

  private applyTextboxResizeBehavior(
    field: DocumentFieldDraft,
    element: HTMLElement,
    fontSettings: ResolvedFieldFontSettings,
  ): void {
    if (field.type !== 'textbox') {
      return;
    }

    const textarea = element.querySelector<HTMLTextAreaElement>(':scope > textarea');
    if (!textarea) {
      return;
    }

    const textboxSettings = this.resolveTextboxSettings(field);
    const pageMetrics = this.pagePixelMetricsForFieldNode(element);
    const baseWidth = Math.max(field.rect.width * pageMetrics.width, 1);
    const baseHeight = Math.max(field.rect.height * pageMetrics.height, 1);
    const measurementText = this.textboxMeasurementText(field);

    textarea.style.whiteSpace = 'pre-wrap';
    textarea.style.overflowWrap = 'anywhere';
    textarea.style.height = '100%';

    if (textboxSettings.resizeType === 'fixed') {
      return;
    }

    if (textboxSettings.resizeType === 'grow_horizontally') {
      const measured = this.measureTextboxContent(measurementText, fontSettings, {
        whiteSpace: 'pre',
        minWidth: baseWidth,
      });
      element.style.width = `${Math.max(baseWidth, measured.width)}px`;
      textarea.style.whiteSpace = 'nowrap';
      return;
    }

    if (textboxSettings.resizeType === 'grow_vertically') {
      const measured = this.measureTextboxContent(measurementText, fontSettings, {
        width: baseWidth,
        minHeight: baseHeight,
        whiteSpace: 'pre-wrap',
      });
      element.style.height = `${Math.max(baseHeight, measured.height)}px`;
      return;
    }

    if (textboxSettings.resizeType === 'grow_both') {
      const measured = this.measureTextboxContent(measurementText, fontSettings, {
        whiteSpace: 'pre-wrap',
        minWidth: baseWidth,
        minHeight: baseHeight,
      });
      element.style.width = `${Math.max(baseWidth, measured.width)}px`;
      element.style.height = `${Math.max(baseHeight, measured.height)}px`;
      return;
    }

    const autoResize = this.resolveAutoResizedFont(fontSettings, measurementText, baseWidth, baseHeight);
    element.style.fontSize = `${autoResize.size}px`;
    element.style.lineHeight = `${autoResize.lineHeight}px`;
    textarea.style.fontSize = `${autoResize.size}px`;
    textarea.style.lineHeight = `${autoResize.lineHeight}px`;
  }

  private applyIdentityFieldResizeBehavior(field: DocumentFieldDraft, element: HTMLElement): void {
    const pageMetrics = this.pagePixelMetricsForFieldNode(element);
    const baseWidth = Math.max(field.rect.width * pageMetrics.width, 1);
    const pageLeft = field.rect.x * pageMetrics.width;
    const maxWidth = Math.max(pageMetrics.width - pageLeft, baseWidth);
    const fontSettings = this.resolveTextboxFontSettings(field);
    const measurementText = this.identityMeasurementText(field);
    const measured = this.measureTextboxContent(measurementText, fontSettings, {
      whiteSpace: 'pre',
      minWidth: baseWidth,
    });

    element.style.width = `${Math.min(Math.max(baseWidth, measured.width), maxWidth)}px`;
  }

  private fieldNodeKind(field: DocumentFieldDraft): FieldNodeKind {
    if (!SUPPORTED_FIELD_TYPES.includes(field.type)) {
      return 'deferred';
    }
    if (field.type === 'signature' || field.type === 'initials') {
      return 'mark';
    }
    if (field.type === 'image') {
      return 'image';
    }
    if (field.type === 'editable_date') {
      return 'editable_date';
    }
    if (field.type === 'checkbox' || field.type === 'radio') {
      return 'choice';
    }
    if (field.type === 'dropdown') {
      return 'dropdown';
    }
    return 'text';
  }

  private removePageFieldNodes(page: number): void {
    for (const [fieldId, entry] of this.fieldNodeCache.entries()) {
      if (entry.page !== page) {
        continue;
      }

      entry.element.remove();
      this.fieldNodeCache.delete(fieldId);
    }
  }

  private removeStaleFieldNodes(renderedFieldIds: Set<string>): void {
    for (const [fieldId, entry] of this.fieldNodeCache.entries()) {
      if (renderedFieldIds.has(fieldId)) {
        continue;
      }

      entry.element.remove();
      this.fieldNodeCache.delete(fieldId);
    }
  }

  private clearFieldNodeCache(): void {
    for (const entry of this.fieldNodeCache.values()) {
      entry.element.remove();
    }
    this.fieldNodeCache.clear();
  }

  private applyFieldRect(element: HTMLElement, rect: DocumentFieldDraft['rect']): void {
    element.style.left = `${rect.x * 100}%`;
    element.style.top = `${rect.y * 100}%`;
    element.style.width = `${rect.width * 100}%`;
    element.style.height = `${rect.height * 100}%`;
  }

  private resolveTextboxFontSettings(field: DocumentFieldDraft): ResolvedFieldFontSettings {
    const font = (field.settings as { font?: Record<string, unknown> } | undefined)?.font;
    if (!font || typeof font !== 'object') {
      return DEFAULT_TEXTBOX_FONT_SETTINGS;
    }

    return {
      family: typeof font['family'] === 'string' ? font['family'] : DEFAULT_TEXTBOX_FONT_SETTINGS.family,
      size: typeof font['size'] === 'number' ? font['size'] : DEFAULT_TEXTBOX_FONT_SETTINGS.size,
      lineHeight: typeof font['lineHeight'] === 'number' ? font['lineHeight'] : DEFAULT_TEXTBOX_FONT_SETTINGS.lineHeight,
      bold: font['bold'] === true,
      italic: font['italic'] === true,
      underline: font['underline'] === true,
      color: typeof font['color'] === 'string' ? font['color'] : DEFAULT_TEXTBOX_FONT_SETTINGS.color,
      align:
        font['align'] === 'center' || font['align'] === 'right' ? font['align'] : DEFAULT_TEXTBOX_FONT_SETTINGS.align,
      characterSpacing: typeof font['characterSpacing'] === 'number' ? font['characterSpacing'] : DEFAULT_TEXTBOX_FONT_SETTINGS.characterSpacing,
      rtl: font['rtl'] === true,
    };
  }

  private resolveTextboxSettings(field: DocumentFieldDraft): ResolvedTextboxSettings {
    const textbox = (field.settings as { textbox?: Record<string, unknown> } | undefined)?.textbox;
    if (!textbox || typeof textbox !== 'object') {
      return DEFAULT_TEXTBOX_SETTINGS;
    }

    const resizeType = textbox['resizeType'];
    return {
      resizeType:
        resizeType === 'grow_horizontally' ||
        resizeType === 'grow_vertically' ||
        resizeType === 'grow_both' ||
        resizeType === 'fixed' ||
        resizeType === 'auto_resize_font'
          ? resizeType
          : DEFAULT_TEXTBOX_SETTINGS.resizeType,
      fixedWidth: textbox['fixedWidth'] === true,
      characterLimit: typeof textbox['characterLimit'] === 'number' ? textbox['characterLimit'] : undefined,
      validationType:
        textbox['validationType'] === 'email' ||
        textbox['validationType'] === 'number' ||
        textbox['validationType'] === 'minimum' ||
        textbox['validationType'] === 'maximum' ||
        textbox['validationType'] === 'regex'
          ? textbox['validationType']
          : DEFAULT_TEXTBOX_SETTINGS.validationType,
      validationValue: typeof textbox['validationValue'] === 'string' ? textbox['validationValue'] : undefined,
    };
  }

  private textboxMeasurementText(field: DocumentFieldDraft): string {
    const value = this.valueForField(field);
    return value || this.emptyFieldPrompt(field, field.placeholder || '') || ' ';
  }

  private identityMeasurementText(field: DocumentFieldDraft): string {
    return this.valueForField(field) || this.emptyFieldPrompt(field, field.placeholder || '') || ' ';
  }

  private pagePixelMetricsForFieldNode(element: HTMLElement): { width: number; height: number } {
    const pageElement = element.closest('.page') as HTMLElement | null;
    if (!pageElement) {
      return { width: DEFAULT_PAGE_PIXEL_WIDTH, height: DEFAULT_PAGE_PIXEL_HEIGHT };
    }

    const rect = pageElement.getBoundingClientRect();
    return {
      width: rect.width || pageElement.clientWidth || DEFAULT_PAGE_PIXEL_WIDTH,
      height: rect.height || pageElement.clientHeight || DEFAULT_PAGE_PIXEL_HEIGHT,
    };
  }

  private ensureTextMeasureElement(): HTMLDivElement {
    if (this.textMeasureElement) {
      return this.textMeasureElement;
    }

    const measure = document.createElement('div');
    measure.setAttribute('aria-hidden', 'true');
    measure.style.position = 'fixed';
    measure.style.left = '-99999px';
    measure.style.top = '0';
    measure.style.visibility = 'hidden';
    measure.style.pointerEvents = 'none';
    measure.style.boxSizing = 'border-box';
    measure.style.padding = `${TEXTBOX_VERTICAL_PADDING_PX}px ${TEXTBOX_HORIZONTAL_PADDING_PX}px`;
    document.body.append(measure);
    this.textMeasureElement = measure;
    return measure;
  }

  private measureTextboxContent(
    text: string,
    font: ResolvedFieldFontSettings,
    options: {
      width?: number;
      minWidth?: number;
      minHeight?: number;
      whiteSpace: 'pre' | 'pre-wrap';
    },
  ): { width: number; height: number } {
    const measure = this.ensureTextMeasureElement();
    measure.style.fontFamily = font.family;
    measure.style.fontSize = `${font.size}px`;
    measure.style.lineHeight = `${font.lineHeight}px`;
    measure.style.fontWeight = font.bold ? '700' : '500';
    measure.style.fontStyle = font.italic ? 'italic' : 'normal';
    measure.style.letterSpacing = `${font.characterSpacing}px`;
    measure.style.direction = font.rtl ? 'rtl' : 'ltr';
    measure.style.textAlign = font.align;
    measure.style.whiteSpace = options.whiteSpace;
    measure.style.overflowWrap = options.whiteSpace === 'pre-wrap' ? 'anywhere' : 'normal';
    measure.style.width = options.width ? `${options.width}px` : 'auto';
    measure.textContent = text || ' ';

    const rect = measure.getBoundingClientRect();
    const approximateWidth = this.approximateTextWidth(text, font);
    const width = Math.max(
      options.minWidth ?? 0,
      rect.width,
      measure.scrollWidth,
      options.width ?? approximateWidth,
      options.width ? 0 : approximateWidth,
    );
    const lineCount =
      options.width && options.whiteSpace === 'pre-wrap'
        ? Math.max(
            1,
            Math.ceil((approximateWidth - TEXTBOX_HORIZONTAL_PADDING_PX * 2) / Math.max(options.width - TEXTBOX_HORIZONTAL_PADDING_PX * 2, 1)),
          )
        : Math.max(1, text.split('\n').length);
    const approximateHeight = lineCount * font.lineHeight + TEXTBOX_VERTICAL_PADDING_PX * 2;
    const height = Math.max(options.minHeight ?? 0, rect.height, measure.scrollHeight, approximateHeight);

    return { width, height };
  }

  private approximateTextWidth(text: string, font: ResolvedFieldFontSettings): number {
    const longestLineLength = text
      .split('\n')
      .reduce((longest, line) => Math.max(longest, line.length), 1);
    const letterSpacingWidth = Math.max(0, longestLineLength - 1) * Math.max(font.characterSpacing, 0);
    return longestLineLength * font.size * APPROXIMATE_CHARACTER_WIDTH_FACTOR + letterSpacingWidth + TEXTBOX_HORIZONTAL_PADDING_PX * 2;
  }

  private resolveAutoResizedFont(
    font: ResolvedFieldFontSettings,
    text: string,
    baseWidth: number,
    baseHeight: number,
  ): { size: number; lineHeight: number } {
    const ratio = font.lineHeight > 0 ? font.lineHeight / Math.max(font.size, 1) : 1;
    let size = font.size;
    let lineHeight = font.lineHeight;

    while (size > MIN_AUTO_RESIZE_FONT_SIZE_PX) {
      const measured = this.measureTextboxContent(text, { ...font, size, lineHeight }, {
        width: baseWidth,
        minHeight: baseHeight,
        whiteSpace: 'pre-wrap',
      });

      if (measured.height <= baseHeight && measured.width <= baseWidth) {
        break;
      }

      size -= 1;
      lineHeight = Math.max(Math.round(size * ratio), size);
    }

    return { size, lineHeight };
  }

  private processMarkUpload(file: File): void {
    this.markUploadError = '';

    if (!MARK_UPLOAD_MIME_TYPES.has(file.type)) {
      this.markUploadDataUrl = null;
      this.markUploadError = 'Unsupported file format. Use PNG, JPG, or BMP.';
      return;
    }

    if (file.size > MAX_MARK_UPLOAD_BYTES) {
      this.markUploadDataUrl = null;
      this.markUploadError = 'File size exceeds 2 MB limit.';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : null;
      if (!result) {
        this.markUploadDataUrl = null;
        this.markUploadError = 'Unable to read file.';
        this.cdr.markForCheck();
        return;
      }

      this.markUploadDataUrl = result;
      this.markUploadError = '';
      this.cdr.markForCheck();
    };

    reader.onerror = () => {
      this.markUploadDataUrl = null;
      this.markUploadError = 'Unable to read file.';
      this.cdr.markForCheck();
    };

    reader.readAsDataURL(file);
  }

  private processImageUpload(file: File): void {
    this.imageModalError = '';

    if (!IMAGE_UPLOAD_MIME_TYPES.has(file.type)) {
      this.imageModalError = 'Unsupported file format. Use JPG, JPEG, SVG, PNG, BMP.';
      return;
    }

    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      this.imageModalError = 'File size exceeds 25 MB limit.';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : null;
      if (!result) {
        this.imageModalError = 'Unable to read file.';
        this.cdr.markForCheck();
        return;
      }

      this.imageSourceDataUrl = result;
      this.imageDraftDataUrl = result;
      if (this.imageFieldId) {
        this.imagePrimarySourceByFieldId.set(this.imageFieldId, result);
      }
      this.imageMarkedForRemoval = false;
      this.resetImageTransform();
      this.imageModalError = '';
      this.cdr.markForCheck();
    };

    reader.onerror = () => {
      this.imageModalError = 'Unable to read file.';
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(file);
  }

  private async renderImageFieldDataUrl(): Promise<string | null> {
    if (!this.imageSourceDataUrl || !this.imageFieldId || !this.session) {
      return null;
    }

    const field = this.session.fields.find((entry) => entry.id === this.imageFieldId);
    if (!field) {
      return null;
    }

    const image = await this.loadImageElement(this.imageSourceDataUrl);
    if (!image) {
      return null;
    }

    const width = Math.max(80, Math.round(field.rect.width * 1200));
    const height = Math.max(60, Math.round(field.rect.height * 1200));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    ctx.clearRect(0, 0, width, height);
    const fitScale = Math.min(width / image.width, height / image.height);
    const drawScale = fitScale * this.imageScale;
    const drawWidth = image.width * drawScale;
    const drawHeight = image.height * drawScale;
    const x = (width - drawWidth) / 2 + this.imageOffsetX;
    const y = (height - drawHeight) / 2 + this.imageOffsetY;
    ctx.drawImage(image, x, y, drawWidth, drawHeight);
    return canvas.toDataURL('image/png');
  }

  private loadImageElement(dataUrl: string): Promise<HTMLImageElement | null> {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => resolve(null);
      image.src = dataUrl;
    });
  }

  private get selectedTypeStyle(): TypedMarkStyleOption {
    return this.typeStyleOptions[this.markTypeStyleIndex] ?? this.typeStyleOptions[0];
  }

  private async ensureTypedMarkFontLoaded(style: TypedMarkStyleOption): Promise<void> {
    if (typeof document === 'undefined' || !document.fonts) {
      return;
    }

    try {
      await document.fonts.load(`${style.fontWeight} 82px ${style.fontFamily}`, this.markTypeInput.trim() || 'Signer');
    } catch {
      // Continue with the current browser font state if the FontFaceSet API is unavailable.
    }
  }

  private makeTypedMarkDataUrl(text: string, style: TypedMarkStyleOption): string | null {
    if (!text) {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 740;
    canvas.height = 220;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    ctx.fillStyle = '#111111';
    ctx.font = `${style.fontWeight} 82px ${style.fontFamily}`;
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 40, canvas.height / 2 + 5);
    return canvas.toDataURL('image/png');
  }

  private async normalizeMarkDataUrl(dataUrl: string): Promise<string | null> {
    const image = await this.loadImageElement(dataUrl);
    if (!image) {
      return null;
    }

    const srcCanvas = document.createElement('canvas');
    srcCanvas.width = image.width;
    srcCanvas.height = image.height;
    const srcCtx = srcCanvas.getContext('2d', { willReadFrequently: true });
    if (!srcCtx) {
      return null;
    }

    srcCtx.drawImage(image, 0, 0);
    const imageData = srcCtx.getImageData(0, 0, srcCanvas.width, srcCanvas.height);
    const bounds = this.detectMarkBounds(imageData);
    if (!bounds) {
      return null;
    }

    const paddedLeft = Math.max(0, bounds.left - MARK_TRIM_PADDING_PX);
    const paddedTop = Math.max(0, bounds.top - MARK_TRIM_PADDING_PX);
    const paddedRight = Math.min(srcCanvas.width - 1, bounds.right + MARK_TRIM_PADDING_PX);
    const paddedBottom = Math.min(srcCanvas.height - 1, bounds.bottom + MARK_TRIM_PADDING_PX);
    const cropWidth = Math.max(1, paddedRight - paddedLeft + 1);
    const cropHeight = Math.max(1, paddedBottom - paddedTop + 1);

    const outCanvas = document.createElement('canvas');
    outCanvas.width = cropWidth;
    outCanvas.height = cropHeight;
    const outCtx = outCanvas.getContext('2d');
    if (!outCtx) {
      return null;
    }

    outCtx.drawImage(srcCanvas, paddedLeft, paddedTop, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
    return outCanvas.toDataURL('image/png');
  }

  private detectMarkBounds(imageData: ImageData): { left: number; top: number; right: number; bottom: number } | null {
    const { data, width, height } = imageData;
    let left = width;
    let right = -1;
    let top = height;
    let bottom = -1;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = (y * width + x) * 4;
        const red = data[index] ?? 255;
        const green = data[index + 1] ?? 255;
        const blue = data[index + 2] ?? 255;
        const alpha = data[index + 3] ?? 0;
        const luma = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
        const isStrokePixel = alpha > MARK_TRIM_ALPHA_THRESHOLD && luma < MARK_TRIM_LUMA_THRESHOLD;
        if (!isStrokePixel) {
          continue;
        }

        if (x < left) {
          left = x;
        }
        if (x > right) {
          right = x;
        }
        if (y < top) {
          top = y;
        }
        if (y > bottom) {
          bottom = y;
        }
      }
    }

    if (right < left || bottom < top) {
      return null;
    }

    return { left, top, right, bottom };
  }

  private prepareDrawCanvas(): void {
    const canvas = this.drawCanvas?.nativeElement;
    if (!canvas) {
      return;
    }

    const width = Math.max(640, Math.floor(canvas.getBoundingClientRect().width));
    const height = 165;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.lineWidth = 2.4;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111111';
    ctx.clearRect(0, 0, width, height);

    if (!this.markDrawHasStroke) {
      return;
    }
  }

  private pointerPosition(canvas: HTMLCanvasElement, event: PointerEvent): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    };
  }

  private fieldDisplayLabel(field: DocumentFieldDraft): string {
    return field.label || field.fieldKey || field.type.replaceAll('_', ' ');
  }

  private emptyFieldPrompt(field: DocumentFieldDraft, fallback = ''): string {
    const baseText = this.fieldDisplayLabel(field).trim() || fallback.trim();
    return field.required ? `${baseText}*` : baseText;
  }

  private getCheckboxPixelSize(field: DocumentFieldDraft): number | null {
    return this.getChoicePixelSizeFromSettings(field, 'checkbox');
  }

  private getRadioPixelSize(field: DocumentFieldDraft): number | null {
    return this.getChoicePixelSizeFromSettings(field, 'radio');
  }

  private getChoicePixelSizeFromSettings(field: DocumentFieldDraft, key: 'checkbox' | 'radio'): number | null {
    if (!field.settings || typeof field.settings !== 'object') {
      return null;
    }

    const settingsRecord = field.settings as Record<string, unknown>;
    const choiceSettings = settingsRecord[key];
    if (!choiceSettings || typeof choiceSettings !== 'object') {
      return null;
    }

    const size = (choiceSettings as Record<string, unknown>)['size'];
    if (typeof size !== 'number' || !Number.isFinite(size) || size <= 0) {
      return null;
    }

    return size;
  }

  private activeField(): DocumentFieldDraft | null {
    if (!this.session || !this.activeFieldId) {
      return null;
    }

    return this.session.fields.find((field) => field.id === this.activeFieldId) ?? null;
  }

  private syncWalkthroughArrow(): void {
    const rail = this.walkthroughRail?.nativeElement;
    if (!rail) {
      return;
    }

    const fallbackTop = this.clampWalkthroughOffset(
      SignRequestComponent.WALKTHROUGH_DEFAULT_TOP_PX,
      rail.clientHeight,
    );
    if (!this.hasUnlockedSigningUi || !this.activeFieldId) {
      this.walkthroughArrowOffset = fallbackTop;
      this.cdr.markForCheck();
      return;
    }

    const activeChip = this.viewerHost?.nativeElement.querySelector<HTMLElement>(
      `.sign-field-chip[data-field-id="${this.activeFieldId}"]`,
    );
    if (!activeChip) {
      this.walkthroughArrowOffset = fallbackTop;
      this.cdr.markForCheck();
      return;
    }

    const railRect = rail.getBoundingClientRect();
    const chipRect = activeChip.getBoundingClientRect();
    const centeredTop =
      chipRect.top + chipRect.height / 2 - railRect.top - SignRequestComponent.WALKTHROUGH_ARROW_HEIGHT_PX / 2;

    this.walkthroughArrowOffset = this.clampWalkthroughOffset(centeredTop, rail.clientHeight);
    this.cdr.markForCheck();
  }

  private clampWalkthroughOffset(offset: number, railHeight: number): number {
    const max = Math.max(
      SignRequestComponent.WALKTHROUGH_EDGE_PADDING_PX,
      railHeight -
        SignRequestComponent.WALKTHROUGH_ARROW_HEIGHT_PX -
        SignRequestComponent.WALKTHROUGH_EDGE_PADDING_PX,
    );
    return this.clamp(offset, SignRequestComponent.WALKTHROUGH_EDGE_PADDING_PX, max);
  }

  private buildDatePopover(fieldId: string): HTMLElement {
    const popover = document.createElement('div');
    popover.className = 'sign-date-popover';
    popover.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    const helper = document.createElement('p');
    helper.className = 'date-helper';
    helper.textContent = 'Select a default date';
    popover.append(helper);

    const header = document.createElement('div');
    header.className = 'date-head';

    const selectors = document.createElement('div');
    selectors.className = 'date-selectors';

    const monthSelect = document.createElement('select');
    monthSelect.className = 'date-select month-select';
    for (const optionConfig of this.datePickerMonthOptions) {
      const option = document.createElement('option');
      option.value = String(optionConfig.value);
      option.textContent = optionConfig.label;
      monthSelect.append(option);
    }
    monthSelect.value = String(this.datePickerMonth.getUTCMonth());
    monthSelect.setAttribute('aria-label', 'Select month');
    monthSelect.addEventListener('change', (event) => {
      event.preventDefault();
      this.zone.run(() => {
        this.setDatePickerMonth(Number((event.target as HTMLSelectElement).value));
      });
    });
    selectors.append(monthSelect);

    const yearSelect = document.createElement('select');
    yearSelect.className = 'date-select year-select';
    for (const year of this.datePickerYearOptions) {
      const option = document.createElement('option');
      option.value = String(year);
      option.textContent = String(year);
      yearSelect.append(option);
    }
    yearSelect.value = String(this.datePickerMonth.getUTCFullYear());
    yearSelect.setAttribute('aria-label', 'Select year');
    yearSelect.addEventListener('change', (event) => {
      event.preventDefault();
      this.zone.run(() => {
        this.setDatePickerYear(Number((event.target as HTMLSelectElement).value));
      });
    });
    selectors.append(yearSelect);

    header.append(selectors);

    const controls = document.createElement('div');
    controls.className = 'date-nav';
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.textContent = '‹';
    prev.setAttribute('aria-label', 'Previous month');
    prev.addEventListener('click', (event) => {
      event.preventDefault();
      this.zone.run(() => this.goToPreviousMonth());
    });
    const next = document.createElement('button');
    next.type = 'button';
    next.textContent = '›';
    next.setAttribute('aria-label', 'Next month');
    next.addEventListener('click', (event) => {
      event.preventDefault();
      this.zone.run(() => this.goToNextMonth());
    });
    controls.append(prev);
    controls.append(next);
    header.append(controls);
    popover.append(header);

    const weekdays = document.createElement('div');
    weekdays.className = 'date-weekdays';
    for (const day of ['S', 'M', 'T', 'W', 'T', 'F', 'S']) {
      const span = document.createElement('span');
      span.textContent = day;
      weekdays.append(span);
    }
    popover.append(weekdays);

    const grid = document.createElement('div');
    grid.className = 'date-grid';
    const current = String(this.fieldValues.get(fieldId) ?? '');
    const selected = this.parseDateInput(current);
    const selectedDateInput = selected ? this.dateTimeFormat.formatDateInput(selected) : null;
    for (const day of this.calendarDays()) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = String(day.date.getUTCDate());
      if (!day.inCurrentMonth) {
        btn.classList.add('muted');
      }
      if (selectedDateInput && this.utcDateInputValue(day.date) === selectedDateInput) {
        btn.classList.add('selected');
      }
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.zone.run(() => this.pickDateForField(fieldId, day.date));
      });
      grid.append(btn);
    }
    popover.append(grid);

    return popover;
  }

  private syncEditableDateValidity(fieldId: string, value: string): void {
    if (!value.trim()) {
      this.invalidEditableDateFields.delete(fieldId);
      return;
    }

    if (this.parseDateInput(value)) {
      this.invalidEditableDateFields.delete(fieldId);
    } else {
      this.invalidEditableDateFields.add(fieldId);
    }
  }

  private syncTextboxValidity(field: DocumentFieldDraft, value: string): void {
    if (field.type !== 'textbox') {
      return;
    }

    if (this.isTextboxValueValid(field, value)) {
      this.invalidTextboxFields.delete(field.id);
      if (this.invalidTextboxFields.size === 0 && this.errorMessage === 'Please correct the highlighted textbox fields before finishing.') {
        this.errorMessage = '';
        this.feedback.clearError();
      }
    } else {
      this.invalidTextboxFields.add(field.id);
    }
  }

  private syncAllTextboxValidity(fields: DocumentFieldDraft[]): void {
    for (const field of fields) {
      if (field.type !== 'textbox') {
        continue;
      }

      this.syncTextboxValidity(field, this.valueForField(field));
    }
  }

  private firstInvalidTextboxField(): DocumentFieldDraft | null {
    return this.sortedSupportedFields().find((field) => field.type === 'textbox' && this.invalidTextboxFields.has(field.id)) ?? null;
  }

  private isTextboxValueValid(field: DocumentFieldDraft, rawValue: string): boolean {
    const textboxSettings = this.resolveTextboxSettings(field);
    const value = rawValue.trim();

    if (!value) {
      return true;
    }

    switch (textboxSettings.validationType) {
      case 'email':
        return EMAIL_PATTERN.test(value);
      case 'number':
        return Number.isFinite(Number(value));
      case 'minimum': {
        const numericValue = Number(value);
        const min = this.parseTextboxValidationThreshold(textboxSettings.validationValue);
        if (!Number.isFinite(numericValue) || min === null) {
          return min === null;
        }
        return numericValue >= min;
      }
      case 'maximum': {
        const numericValue = Number(value);
        const max = this.parseTextboxValidationThreshold(textboxSettings.validationValue);
        if (!Number.isFinite(numericValue) || max === null) {
          return max === null;
        }
        return numericValue <= max;
      }
      case 'regex':
      case 'none':
      default:
        return true;
    }
  }

  private parseTextboxValidationThreshold(value: string | undefined): number | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private parseDateInput(value: string): Date | null {
    return this.dateTimeFormat.parseFormattedDate(value);
  }

  private yearOptionsForDatePicker(): number[] {
    const currentYear = Number(this.dateTimeFormat.formatDateInput(new Date()).slice(0, 4));
    const selectedYear = this.datePickerMonth.getUTCFullYear();
    const startYear = Math.min(DATE_PICKER_MIN_YEAR, selectedYear);
    const endYear = Math.max(currentYear + DATE_PICKER_FUTURE_YEAR_BUFFER, selectedYear);
    const years: number[] = [];

    for (let year = startYear; year <= endYear; year += 1) {
      years.push(year);
    }

    return years;
  }

  private formatDate(date: Date): string {
    return this.dateTimeFormat.formatDate(date);
  }

  private applyPrefillDefaults(session: SignSessionResponse): void {
    for (const field of session.fields) {
      if (field.type === 'date_signed') {
        this.fieldValues.set(field.id, this.localDateString());
        continue;
      }

      if (field.type === 'checkbox' || field.type === 'radio') {
        continue;
      }

      if (field.type === 'dropdown') {
        const normalizedExisting = this.dropdownNormalizedValue(field, this.fieldValues.get(field.id));
        if (normalizedExisting) {
          this.fieldValues.set(field.id, normalizedExisting);
          continue;
        }

        this.fieldValues.set(field.id, this.dropdownDefaultValue(field) ?? '');
        continue;
      }

      if (this.valueForField(field).trim()) {
        continue;
      }

      if (field.type === 'name') {
        this.fieldValues.set(field.id, session.recipient.name || '');
      } else if (field.type === 'email') {
        this.fieldValues.set(field.id, session.recipient.email || '');
      }
    }

    const handledChoiceGroups = new Set<string>();
    for (const field of session.fields) {
      if (field.type !== 'checkbox' && field.type !== 'radio') {
        continue;
      }

      const type = field.type;
      const snapshot = this.choiceGroupSnapshot(field, type);
      const groupToken = `${type}:${snapshot.groupKey}`;
      if (handledChoiceGroups.has(groupToken)) {
        continue;
      }
      handledChoiceGroups.add(groupToken);

      if (type === 'checkbox') {
        this.applyCheckboxGroupPrefill(snapshot);
      } else {
        this.applyRadioGroupPrefill(snapshot);
      }
    }
  }

  private applyCheckboxGroupPrefill(snapshot: ChoiceGroupSnapshot): void {
    const hasPersistedValues = snapshot.fields.some((field) => this.fieldValues.has(field.id));
    const selectedOptions = new Set<string>();

    if (hasPersistedValues) {
      for (const field of snapshot.fields) {
        const option = this.choiceValueForGroupField(snapshot, field, 'checkbox');
        if (!option) {
          continue;
        }
        if (this.isChoiceSelected(this.fieldValues.get(field.id))) {
          selectedOptions.add(option);
        }
      }
    } else {
      for (const option of this.checkboxDefaultValuesForSnapshot(snapshot)) {
        selectedOptions.add(option);
      }
    }

    for (const field of snapshot.fields) {
      const option = this.choiceValueForGroupField(snapshot, field, 'checkbox');
      this.fieldValues.set(field.id, !!option && selectedOptions.has(option));
    }
  }

  private applyRadioGroupPrefill(snapshot: ChoiceGroupSnapshot): void {
    const persistedSelection = snapshot.fields.find((field) => this.isChoiceSelected(this.fieldValues.get(field.id))) ?? null;
    const defaultValue = this.radioDefaultValueForSnapshot(snapshot);
    const defaultSelection = defaultValue
      ? snapshot.fields.find((field) => this.choiceValueForGroupField(snapshot, field, 'radio') === defaultValue) ?? null
      : null;
    const checkedSelection = snapshot.fields.find((field) => this.radioDefaultChecked(field)) ?? null;
    const preferred = persistedSelection ?? defaultSelection ?? checkedSelection ?? null;

    for (const field of snapshot.fields) {
      this.fieldValues.set(field.id, !!preferred && preferred.id === field.id);
    }
  }

  private sortedSupportedFields(): DocumentFieldDraft[] {
    return [...this.supportedFields].sort((a, b) => {
      if (a.page !== b.page) {
        return a.page - b.page;
      }

      if (a.rect.y !== b.rect.y) {
        return a.rect.y - b.rect.y;
      }

      return a.rect.x - b.rect.x;
    });
  }

  private nextIncompleteRequiredField(): DocumentFieldDraft | null {
    const units = this.requiredFieldUnits();
    if (units.length === 0) {
      return null;
    }

    const activeIndex = this.activeFieldId ? units.findIndex((unit) => unit.fields.some((field) => field.id === this.activeFieldId)) : -1;

    for (let index = activeIndex + 1; index < units.length; index += 1) {
      if (!units[index]!.fields.some((field) => this.isFilled(field))) {
        return units[index]!.fields[0] ?? null;
      }
    }

    for (let index = 0; index <= activeIndex; index += 1) {
      if (!units[index]!.fields.some((field) => this.isFilled(field))) {
        return units[index]!.fields[0] ?? null;
      }
    }

    return null;
  }

  private isFilled(field: DocumentFieldDraft): boolean {
    const value = this.fieldValues.get(field.id);

    if (field.type === 'checkbox') {
      return this.isChoiceSelected(value);
    }
    if (field.type === 'radio') {
      return this.radioGroupFields(field).some((entry) => this.isChoiceSelected(this.fieldValues.get(entry.id)));
    }

    if (field.type === 'editable_date') {
      if (typeof value !== 'string') {
        return false;
      }

      const trimmed = value.trim();
      if (!trimmed) {
        return false;
      }

      return this.parseDateInput(trimmed) !== null;
    }

    if (field.type === 'dropdown') {
      return this.dropdownNormalizedValue(field, value) !== null;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value);
    }

    if (typeof value === 'boolean') {
      return value;
    }

    return false;
  }

  private radioGroupFields(field: DocumentFieldDraft): DocumentFieldDraft[] {
    if (!this.session || field.type !== 'radio') {
      return [field];
    }
    return this.radioGroupSnapshot(field).fields;
  }

  private checkboxGroupFields(field: DocumentFieldDraft): DocumentFieldDraft[] {
    if (!this.session || field.type !== 'checkbox') {
      return [field];
    }
    return this.checkboxGroupSnapshot(field).fields;
  }

  private radioGroupKey(field: DocumentFieldDraft): string {
    if (field.type !== 'radio') {
      return field.id;
    }
    return this.radioGroupSnapshot(field).groupKey;
  }

  private checkboxGroupKey(field: DocumentFieldDraft): string {
    if (field.type !== 'checkbox') {
      return field.id;
    }
    return this.checkboxGroupSnapshot(field).groupKey;
  }

  private radioGroupSnapshot(field: DocumentFieldDraft): ChoiceGroupSnapshot {
    return this.choiceGroupSnapshot(field, 'radio');
  }

  private checkboxGroupSnapshot(field: DocumentFieldDraft): ChoiceGroupSnapshot {
    return this.choiceGroupSnapshot(field, 'checkbox');
  }

  private choiceGroupSnapshot(field: DocumentFieldDraft, type: ChoiceFieldType): ChoiceGroupSnapshot {
    const context = this.choiceGroupContextForField(field, type);
    return {
      groupKey: context.groupKey,
      fields: context.fields,
      values: this.canonicalChoiceGroupValues(context.fields, type),
    };
  }

  private choiceGroupContextForField(
    field: DocumentFieldDraft,
    type: ChoiceFieldType,
  ): { groupKey: string; fields: DocumentFieldDraft[] } {
    if (!this.session) {
      return { groupKey: field.id, fields: [field] };
    }

    const groupKey = this.resolveChoiceGroupKey(field, type);
    const candidates = this.session.fields.filter(
      (entry) => entry.type === type && entry.page === field.page && entry.role === field.role,
    );
    const anchorExplicitGroupKey = this.choiceExplicitGroupKey(field, type);
    const anchorHasStableGroupKey = !!anchorExplicitGroupKey && !this.isLegacyAutoGroupKey(anchorExplicitGroupKey);
    const grouped = new Map<string, DocumentFieldDraft>();

    if (anchorHasStableGroupKey) {
      for (const candidate of candidates) {
        const explicit = this.choiceExplicitGroupKey(candidate, type);
        if (explicit === anchorExplicitGroupKey) {
          grouped.set(candidate.id, candidate);
        }
      }
    }

    for (const candidate of candidates) {
      const explicit = this.choiceExplicitGroupKey(candidate, type);
      if (explicit === groupKey) {
        grouped.set(candidate.id, candidate);
      }
    }

    for (const candidate of candidates) {
      const explicit = this.choiceExplicitGroupKey(candidate, type);
      if ((!explicit || this.isLegacyAutoGroupKey(explicit)) && this.hasSharedChoiceProfile(field, candidate, type)) {
        grouped.set(candidate.id, candidate);
      }
    }

    grouped.set(field.id, field);
    return {
      groupKey,
      fields: [...grouped.values()].sort((a, b) => (a.rect.y === b.rect.y ? a.rect.x - b.rect.x : a.rect.y - b.rect.y)),
    };
  }

  private choiceGroupFields(field: DocumentFieldDraft, type: 'checkbox' | 'radio'): DocumentFieldDraft[] {
    return this.choiceGroupContextForField(field, type).fields;
  }

  private resolveChoiceGroupKey(field: DocumentFieldDraft, type: 'checkbox' | 'radio'): string {
    const explicit = this.choiceExplicitGroupKey(field, type);
    if (explicit && !this.isLegacyAutoGroupKey(explicit)) {
      return explicit;
    }

    const candidates = this.session
      ? this.session.fields.filter((entry) => entry.type === type && entry.page === field.page && entry.role === field.role)
      : [];

    if (candidates.length > 0) {
      const siblingKey = candidates
        .filter((entry) => entry.id !== field.id)
        .filter((entry) => this.hasSharedChoiceProfile(field, entry, type))
        .map((entry) => this.choiceExplicitGroupKey(entry, type))
        .find((value) => !!value && !this.isLegacyAutoGroupKey(value));

      if (siblingKey) {
        return siblingKey;
      }
    }

    const trimmedFieldKey = field.fieldKey?.trim();
    if (trimmedFieldKey) {
      return trimmedFieldKey;
    }

    return this.choiceFallbackGroupKey(field, type, candidates);
  }

  private choiceFallbackGroupKey(
    field: DocumentFieldDraft,
    type: 'checkbox' | 'radio',
    candidates: DocumentFieldDraft[],
  ): string {
    const prefix = type === 'checkbox' ? 'CheckboxGroup' : 'RadioGroup';
    if (candidates.length === 0) {
      return `${prefix}1`;
    }

    const ordered = [...candidates].sort((a, b) => (a.rect.y === b.rect.y ? a.rect.x - b.rect.x : a.rect.y - b.rect.y));
    const index = ordered.findIndex((entry) => entry.id === field.id);
    if (index >= 0) {
      return `${prefix}${index + 1}`;
    }
    return `${prefix}1`;
  }

  private choiceExplicitGroupKey(field: DocumentFieldDraft, type: 'checkbox' | 'radio'): string | undefined {
    if (!field.settings || typeof field.settings !== 'object') {
      return undefined;
    }
    const settings = field.settings as Record<string, unknown>;
    if (type === 'checkbox') {
      const checkboxSettings = settings['checkbox'] as Record<string, unknown> | undefined;
      const groupId = typeof checkboxSettings?.['optionGroupId'] === 'string' ? checkboxSettings['optionGroupId'].trim() : '';
      return groupId || undefined;
    }
    const radioSettings = settings['radio'] as Record<string, unknown> | undefined;
    const groupName = typeof radioSettings?.['groupName'] === 'string' ? radioSettings['groupName'].trim() : '';
    return groupName || undefined;
  }

  private choiceValues(field: DocumentFieldDraft, type: 'checkbox' | 'radio'): string[] {
    if (!field.settings || typeof field.settings !== 'object') {
      return [type === 'checkbox' ? 'Checkbox1' : 'Radio1'];
    }
    const settings = field.settings as Record<string, unknown>;
    const choiceSettings = settings[type] as Record<string, unknown> | undefined;
    const rawValues = Array.isArray(choiceSettings?.['values']) ? choiceSettings?.['values'] : [];
    const values = rawValues
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter((entry) => entry.length > 0);
    if (values.length > 0) {
      return Array.from(new Set(values));
    }

    const rawValue = typeof choiceSettings?.['value'] === 'string' ? choiceSettings['value'].trim() : '';
    return [rawValue || (type === 'checkbox' ? 'Checkbox1' : 'Radio1')];
  }

  private canonicalChoiceGroupValues(fields: DocumentFieldDraft[], type: ChoiceFieldType): string[] {
    const fallback = type === 'checkbox' ? 'Checkbox1' : 'Radio1';
    const valueLists = fields.map((field) => this.choiceValues(field, type));
    let baseList = valueLists[0] ? [...valueLists[0]] : [fallback];

    for (const candidate of valueLists) {
      if (candidate.length > baseList.length) {
        baseList = [...candidate];
      }
    }

    const ordered: string[] = [];
    const pushUnique = (value: string | undefined): void => {
      const trimmed = value?.trim();
      if (!trimmed || ordered.includes(trimmed)) {
        return;
      }
      ordered.push(trimmed);
    };

    for (const value of baseList) {
      pushUnique(value);
    }
    for (const field of fields) {
      pushUnique(this.choiceValue(field, type));
    }
    for (const values of valueLists) {
      for (const value of values) {
        pushUnique(value);
      }
    }

    return this.sanitizeChoiceValues(ordered, fallback);
  }

  private choiceValue(field: DocumentFieldDraft, type: 'checkbox' | 'radio'): string | undefined {
    if (!field.settings || typeof field.settings !== 'object') {
      return undefined;
    }
    const settings = field.settings as Record<string, unknown>;
    const choiceSettings = settings[type] as Record<string, unknown> | undefined;
    const value = typeof choiceSettings?.['value'] === 'string' ? choiceSettings['value'].trim() : '';
    return value || undefined;
  }

  private hasSharedChoiceProfile(
    anchor: DocumentFieldDraft,
    candidate: DocumentFieldDraft,
    type: 'checkbox' | 'radio',
  ): boolean {
    if (anchor.id === candidate.id) {
      return true;
    }
    const anchorValues = this.choiceValues(anchor, type);
    const candidateValues = this.choiceValues(candidate, type);
    const anchorValue = this.choiceValue(anchor, type);
    const candidateValue = this.choiceValue(candidate, type);

    if (anchorValue && candidateValues.includes(anchorValue)) {
      return true;
    }
    if (candidateValue && anchorValues.includes(candidateValue)) {
      return true;
    }
    if (anchorValues.length === 0 || candidateValues.length === 0) {
      return false;
    }
    return anchorValues.join('\u0000') === candidateValues.join('\u0000');
  }

  private choiceValueForGroupField(
    snapshot: ChoiceGroupSnapshot,
    field: DocumentFieldDraft,
    type: ChoiceFieldType,
  ): string | undefined {
    const explicit = this.choiceValue(field, type);
    if (explicit && snapshot.values.includes(explicit)) {
      return explicit;
    }

    const index = snapshot.fields.findIndex((entry) => entry.id === field.id);
    if (index >= 0) {
      return snapshot.values[index] ?? snapshot.values[0];
    }

    return snapshot.values[0];
  }

  private checkboxDefaultChecked(field: DocumentFieldDraft): boolean {
    if (!field.settings || typeof field.settings !== 'object') {
      return false;
    }
    const settings = field.settings as Record<string, unknown>;
    const checkboxSettings = settings['checkbox'] as Record<string, unknown> | undefined;
    return checkboxSettings?.['checked'] === true;
  }

  private checkboxDefaultValues(field: DocumentFieldDraft): string[] {
    if (!field.settings || typeof field.settings !== 'object') {
      return [];
    }
    const settings = field.settings as Record<string, unknown>;
    const checkboxSettings = settings['checkbox'] as Record<string, unknown> | undefined;
    const values = Array.isArray(checkboxSettings?.['defaultValues']) ? checkboxSettings['defaultValues'] : [];
    return this.sanitizeChoiceValues(values, '');
  }

  private checkboxDefaultValuesForSnapshot(snapshot: ChoiceGroupSnapshot): string[] {
    const defaults = new Set<string>();
    for (const field of snapshot.fields) {
      const option = this.choiceValueForGroupField(snapshot, field, 'checkbox');
      if (this.checkboxDefaultChecked(field) && option && snapshot.values.includes(option)) {
        defaults.add(option);
      }
      for (const value of this.checkboxDefaultValues(field)) {
        if (snapshot.values.includes(value)) {
          defaults.add(value);
        }
      }
    }
    return snapshot.values.filter((value) => defaults.has(value));
  }

  private radioDefaultValue(field: DocumentFieldDraft): string | undefined {
    if (!field.settings || typeof field.settings !== 'object') {
      return undefined;
    }
    const settings = field.settings as Record<string, unknown>;
    const radioSettings = settings['radio'] as Record<string, unknown> | undefined;
    const value = typeof radioSettings?.['defaultValue'] === 'string' ? radioSettings['defaultValue'].trim() : '';
    return value || undefined;
  }

  private radioDefaultValueForSnapshot(snapshot: ChoiceGroupSnapshot): string | undefined {
    for (const field of snapshot.fields) {
      const defaultValue = this.radioDefaultValue(field);
      if (defaultValue && snapshot.values.includes(defaultValue)) {
        return defaultValue;
      }
    }

    const checkedField = snapshot.fields.find((field) => this.radioDefaultChecked(field));
    if (!checkedField) {
      return undefined;
    }

    const checkedValue = this.choiceValueForGroupField(snapshot, checkedField, 'radio');
    if (!checkedValue || !snapshot.values.includes(checkedValue)) {
      return undefined;
    }
    return checkedValue;
  }

  private isLegacyAutoGroupKey(value: string | undefined): boolean {
    return !!value && LEGACY_AUTO_GROUP_KEY_PATTERN.test(value);
  }

  private radioDefaultChecked(field: DocumentFieldDraft): boolean {
    if (!field.settings || typeof field.settings !== 'object') {
      return false;
    }
    const settings = field.settings as Record<string, unknown>;
    const radioSettings = settings['radio'] as Record<string, unknown> | undefined;
    return radioSettings?.['checked'] === true;
  }

  private requiredFieldUnits(): Array<{ key: string; fields: DocumentFieldDraft[] }> {
    const required = this.sortedSupportedFields().filter((field) => field.required);
    const units: Array<{ key: string; fields: DocumentFieldDraft[] }> = [];
    const seen = new Set<string>();
    for (const field of required) {
      if (field.type === 'checkbox') {
        const snapshot = this.checkboxGroupSnapshot(field);
        const key = `checkbox:${snapshot.groupKey}`;
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        units.push({ key, fields: snapshot.fields });
        continue;
      }
      if (field.type === 'radio') {
        const snapshot = this.radioGroupSnapshot(field);
        const key = `radio:${snapshot.groupKey}`;
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        units.push({ key, fields: snapshot.fields });
        continue;
      }
      units.push({ key: `field:${field.id}`, fields: [field] });
    }
    return units;
  }

  private isChoiceSelected(value: unknown): boolean {
    return value === true || value === 'true' || value === 1;
  }

  private sanitizeChoiceValues(values: unknown[], fallback: string): string[] {
    const normalized = values
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter((entry) => entry.length > 0);
    const deduped = Array.from(new Set(normalized));
    if (deduped.length > 0) {
      return deduped;
    }
    return fallback ? [fallback] : [];
  }

  private dropdownOptions(field: DocumentFieldDraft): string[] {
    const options = (field.settings as { dropdown?: { options?: unknown } } | undefined)?.dropdown?.options;
    if (!Array.isArray(options)) {
      return [];
    }

    return options
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter((entry): entry is string => entry.length > 0);
  }

  private dropdownHint(field: DocumentFieldDraft): string {
    const hint = (field.settings as { dropdown?: { hintText?: unknown } } | undefined)?.dropdown?.hintText;
    if (typeof hint === 'string' && hint.trim()) {
      return hint.trim();
    }

    return field.placeholder?.trim() || 'Select';
  }

  private dropdownDefaultValue(field: DocumentFieldDraft): string | null {
    const defaultOption = (field.settings as { dropdown?: { defaultOption?: unknown } } | undefined)?.dropdown?.defaultOption;
    return this.dropdownNormalizedValue(field, defaultOption);
  }

  private dropdownNormalizedValue(field: DocumentFieldDraft, value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    return this.dropdownOptions(field).includes(trimmed) ? trimmed : null;
  }

  private defaultSignerName(): string {
    const name = this.session?.recipient?.name?.trim();
    return name || 'Signer';
  }

  private defaultInitials(): string {
    const fullName = this.defaultSignerName();
    const initials = fullName
      .split(' ')
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('');

    return initials || 'IN';
  }

  private localDateString(): string {
    return this.dateTimeFormat.formatDate(new Date());
  }

  private signSessionPreferences(): UserDateTimePreferences | undefined {
    return this.session?.preferences;
  }

  private datePickerMonthFromInput(value: string): Date {
    const parsed = this.dateTimeFormat.parseDateInput(value);
    const normalized = parsed ? this.dateTimeFormat.formatDateInput(parsed) : this.dateTimeFormat.formatDateInput(new Date());
    const [yearText = '0', monthText = '1'] = normalized.split('-');
    return new Date(Date.UTC(Number(yearText), Number(monthText) - 1, 1));
  }

  private datePickerLabelDate(value: Date): Date {
    return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), 15, 12));
  }

  private utcDateInputValue(value: Date): string {
    const year = String(value.getUTCFullYear()).padStart(4, '0');
    const month = String(value.getUTCMonth() + 1).padStart(2, '0');
    const day = String(value.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private extractPageNumber(event: unknown): number | null {
    if (typeof event === 'number' && Number.isInteger(event)) {
      return event;
    }

    const fromCurrent = (event as { currentPageNumber?: unknown })?.currentPageNumber;
    if (typeof fromCurrent === 'number' && Number.isInteger(fromCurrent)) {
      return fromCurrent;
    }

    const fromPage = (event as { pageNumber?: unknown })?.pageNumber;
    if (typeof fromPage === 'number' && Number.isInteger(fromPage)) {
      return fromPage;
    }

    return null;
  }

  private zoomAsNumber(): number {
    const current = Number(this.zoomLevel);
    if (Number.isFinite(current) && current > 0) {
      return current;
    }

    return 1;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private consentStorageKey(): string {
    return `${SignRequestComponent.CONSENT_STORAGE_PREFIX}${this.token}`;
  }

  private readConsentState(): boolean {
    if (!this.token) {
      return false;
    }

    try {
      return localStorage.getItem(this.consentStorageKey()) === '1';
    } catch {
      return false;
    }
  }

  private persistConsentState(): void {
    if (!this.token) {
      return;
    }

    try {
      localStorage.setItem(this.consentStorageKey(), '1');
    } catch {
      // Ignore storage failures and keep runtime behavior.
    }
  }
}
